import {
  ArrowRight,
  Award,
  BookOpen,
  Boxes,
  CheckSquare,
  ChevronRight,
  Clock,
  Code2,
  Compass,
  ExternalLink,
  FileText,
  Filter,
  Flame,
  GraduationCap,
  Hammer,
  Lightbulb,
  Lock,
  MessageSquareText,
  PlugZap,
  Scale,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Workflow,
  Wrench,
} from 'lucide-react'
import { useAppAuth } from '../lib/auth/app-auth'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import { Component, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { api } from '../../convex/_generated/api'
import {
  ActionButton,
  FormStatus,
  SearchEmptyState,
  VideoConsentGate,
} from './app-shell'
import { FaqAccordion } from './faq-accordion'
// The landing page lives in its own leaf module (`home-page.tsx`) so the two
// index routes (`/`, `/de/`) only pull that small file - not this whole
// multi-page module and its Convex/auth/search graph - into the landing's
// initial JS. The two small catalog leaf cards below are shared between the
// landing and the page components in this file; they live in their own leaf
// module so the landing can render them without importing this file.
import { CourseCard, LessonRow } from './catalog-cards'
import { ProBadge, ProCourseCallout, ProGatedContent } from './pro-gate'
import { SettingsNav } from './settings-nav'
import { AdminBillingPanel } from './admin-billing-panel'
import { AdminEmailPanel } from './admin-email-panel'
import { SettingsNewsletter } from './settings-newsletter'
import { PRO_GATED_ID } from '../lib/paywall'
import { LessonNextStep, ProPathCard } from './upsell'
import { TrackOnMount } from './analytics-runtime'
import { trackEvent } from '../lib/analytics'
import { captureAnalyticsException } from '../lib/analytics/posthog-client'
import { ToolLeadCapture } from './newsletter-opt-in'
// PageIntro + NotFoundPage live in their own tiny module so the global 404
// (rendered by __root.tsx) does not pull this whole page module - and the full
// content catalog - into the eager root bundle. Re-exported below so existing
// `import { NotFoundPage } from './pages'` sites keep working.
import { NotFoundPage, PageIntro } from './not-found'
import { slugify } from '../content/slug'
import {
  buildReadingMinutes,
  comparisonReadingMinutes,
  fundamentalReadingMinutes,
  glossaryReadingMinutes,
  guideReadingMinutes,
  toolReadingMinutes,
} from '../content/entity-reading-time'
import type {
  BuildCategory,
  CommunityPost,
  Comparison,
  Fundamental,
  FundamentalCategory,
  GlossaryTerm,
  Guide,
  Lesson,
  LessonSection,
  Resource,
  Tool,
} from '../content/catalog'
import {
  anonIdCookieName,
  activityToDailyList,
  lastVisitStorageName,
  parseAnonymousState,
  pendingStateStorageName,
  recordDailyActivity,
} from '../lib/anonymous-state'
import {
  certificateEligibility,
  computeStreakStats,
  utcDateKey,
} from '../lib/learning-stats'
import { getCookie } from '../lib/cookies'
import {
  buildBlogPostingJsonLd,
  buildBreadcrumbJsonLd,
  buildCourseJsonLd,
  buildDefinedTermJsonLd,
  buildFaqJsonLd,
  buildHowToJsonLd,
  buildItemListJsonLd,
  buildLessonJsonLd,
  buildPersonJsonLd,
  buildResourceJsonLd,
  buildSoftwareAppJsonLd,
  getSiteUrl,
} from '../lib/seo'
import { buildSearchResults } from '../lib/search'
import { useCatalog, useLegalDocument } from '../content/catalog-context'
import type { Catalog } from '../content/catalog-api'
import {
  LOCALE_HTML_LANG,
  LOCALE_INTL,
  localePrefix,
  localizedPath,
  useTranslation,
} from '../lib/i18n'

type FormStatusState = 'idle' | 'saving' | 'success' | 'error'
type CommentTargetType = 'lesson' | 'post' | 'resource' | 'changelog'

export function CoursesPage() {
  const { locale, t, dict } = useTranslation()
  const catalog = useCatalog()
  const lessonsByCourse = useMemo(
    () =>
      catalog.courses.map((course) => ({
        course,
        lessons: catalog.getLessonsForCourse(course.slug),
      })),
    [catalog],
  )
  return (
    <main className="page-shell">
      <PageIntro
        eyebrow={t('pages.courses.eyebrow')}
        title={t('pages.courses.title')}
        text={t('pages.courses.text')}
        actions={
          <>
            <a
              className="primary-button"
              href={localizedPath('course', locale, {
                courseSlug: 'foundations',
              })}
            >
              {t('pages.common.starterPathOpen')}
              <ArrowRight size={18} />
            </a>
            <a
              className="ghost-button"
              href={localizedPath('resources', locale)}
            >
              {t('pages.common.viewResources')}
            </a>
          </>
        }
      />
      <div className="filter-bar">
        <span className="filter-bar-label">
          <Filter size={14} aria-hidden="true" /> {t('pages.common.filter')}
        </span>
        {dict.pages.courses.filters.map((filter) => (
          <button key={filter} type="button" className="filter-chip">
            {filter}
          </button>
        ))}
      </div>
      <div className="course-grid">
        {catalog.courses.map((course) => (
          <CourseCard key={course.slug} course={course} />
        ))}
      </div>
      <div className="lesson-program">
        <div className="section-heading" style={{ marginBottom: '32px' }}>
          <span className="eyebrow">{t('pages.courses.programEyebrow')}</span>
          <h2>{t('pages.courses.programTitle')}</h2>
          <p>{t('pages.courses.programLead')}</p>
        </div>
        <div className="lesson-program-groups">
          {lessonsByCourse.map(({ course, lessons }) => (
            <section key={course.slug} className="lesson-program-group">
              <header className="lesson-program-group-head">
                <div>
                  <span className="eyebrow">
                    {course.order.toString().padStart(2, '0')} ·{' '}
                    {course.lessonCount} {t('pages.common.lessonsWord')}
                  </span>
                  <h3>{course.title}</h3>
                  <p>{course.subtitle}</p>
                </div>
                <a
                  className="ghost-button compact"
                  href={localizedPath('course', locale, {
                    courseSlug: course.slug,
                  })}
                >
                  {t('pages.courses.openCourse')}
                  <ArrowRight size={14} />
                </a>
              </header>
              <div className="lesson-list">
                {lessons.map((lesson) => (
                  <LessonRow key={lesson.slug} lesson={lesson} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </main>
  )
}

/**
 * Certificate CTA for a single course. Shows only once every lesson of the
 * course is completed. Signed-in users get an "issue / view certificate" button
 * (issued + validated server-side); anonymous users get a disabled "sign in to
 * claim" affordance. Reads progress reactively (Convex for signed-in, local
 * state for anonymous) so it lights up the moment 7/7 is reached.
 */
function CourseCertificateCta({ courseSlug }: { courseSlug: string }) {
  const { locale, t } = useTranslation()
  const { isSignedIn } = useAppAuth()
  const stats = useQuery(api.learning.stats, isSignedIn ? {} : 'skip')
  const certificates = useQuery(
    api.learning.myCertificates,
    isSignedIn ? {} : 'skip',
  )
  const issueCertificate = useMutation(api.learning.issueCertificate)
  const [eligible, setEligible] = useState(false)
  const [issuing, setIssuing] = useState(false)
  const [error, setError] = useState(false)

  const catalog = useCatalog()
  const lessonSlugs = useMemo(
    () =>
      catalog.getLessonsForCourse(courseSlug).map((lesson) => lesson.slug),
    [catalog, courseSlug],
  )

  // Anonymous eligibility from local progress.
  useEffect(() => {
    if (typeof window === 'undefined' || isSignedIn) return
    const state = readLocalAnonymousState()
    const completed = Object.entries(state.progress)
      .filter(([, value]) => value.completed)
      .map(([slug]) => slug)
    setEligible(
      certificateEligibility({
        courseLessonSlugs: lessonSlugs,
        completedLessonSlugs: completed,
      }).eligible,
    )
  }, [isSignedIn, lessonSlugs])

  const serverCourse = stats?.courses.find(
    (item) => item.courseSlug === courseSlug,
  )
  const serverEligible = serverCourse?.eligibleForCertificate ?? false
  const existingCertId = certificates?.find(
    (cert) => cert.courseSlug === courseSlug,
  )?.certificateId

  const showCta = isSignedIn ? serverEligible : eligible
  if (!showCta) return null

  async function onIssue() {
    setError(false)
    setIssuing(true)
    try {
      const result = await issueCertificate({ courseSlug })
      window.location.href = localizedPath('certificate', locale, {
        certificateId: result.certificateId,
      })
    } catch {
      setError(true)
      setIssuing(false)
    }
  }

  return (
    <div className="side-panel-stat course-certificate-cta">
      <span className="eyebrow">{t('pages.dashboard.certificateReady')}</span>
      {!isSignedIn ? (
        <a className="ghost-button" href={localizedPath('register', locale)}>
          <Lock size={16} />
          {t('pages.certificate.ctaSignIn')}
        </a>
      ) : existingCertId ? (
        <a
          className="primary-button"
          href={localizedPath('certificate', locale, {
            certificateId: existingCertId,
          })}
        >
          <Award size={16} />
          {t('pages.certificate.ctaView')}
        </a>
      ) : (
        <button
          className="primary-button"
          type="button"
          disabled={issuing}
          onClick={onIssue}
        >
          <Award size={16} />
          {issuing
            ? t('pages.certificate.ctaGetting')
            : t('pages.certificate.ctaGet')}
        </button>
      )}
      {error ? (
        <p className="form-status" role="status">
          {t('pages.certificate.ctaError')}
        </p>
      ) : null}
    </div>
  )
}

export function CoursePage({ courseSlug }: { courseSlug: string }) {
  const { locale, t } = useTranslation()
  const catalog = useCatalog()
  const course = catalog.getCourse(courseSlug)
  if (!course) return <NotFoundPage label={t('pages.notFound.course')} />
  const lessons = catalog.getLessonsForCourse(course.slug)
  const firstLesson = lessons.length > 0 ? lessons[0] : null
  const publishedCount = lessons.filter(
    (lesson) => lesson.status === 'published',
  ).length
  const jsonLd = [
    buildCourseJsonLd({
      title: course.title,
      description: course.description,
      path: localizedPath('course', locale, { courseSlug: course.slug }),
      lessons,
      tier: course.tier,
      locale,
    }),
    buildBreadcrumbJsonLd([
      { name: t('pages.breadcrumbs.start'), path: localizedPath('home', locale) },
      {
        name: t('pages.breadcrumbs.courses'),
        path: localizedPath('courses', locale),
      },
      {
        name: course.title,
        path: localizedPath('course', locale, { courseSlug: course.slug }),
      },
    ]),
  ]

  return (
    <main className="page-shell">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <PageIntro
        eyebrow={`${t('pages.course.eyebrowPrefix')} ${course.order
          .toString()
          .padStart(2, '0')}`}
        title={course.title}
        text={course.description}
      />
      {course.tier === 'pro' ? <ProCourseCallout /> : null}
      <div className="course-summary-strip">
        <div>
          <strong>{lessons.length}</strong>
          <span>{t('pages.course.lessonsLabel')}</span>
        </div>
        <div>
          <strong>
            {course.estimatedMinutes} {t('pages.common.minutesShort')}
          </strong>
          <span>{t('pages.course.estimatedLabel')}</span>
        </div>
        <div>
          <strong>
            {publishedCount}/{lessons.length}
          </strong>
          <span>{t('pages.course.availableLabel')}</span>
        </div>
        {firstLesson ? (
          <a
            className="primary-button"
            href={localizedPath('lesson', locale, {
              courseSlug: course.slug,
              lessonSlug: firstLesson.slug,
            })}
          >
            {t('pages.course.startFirstLesson')}
            <ArrowRight size={16} />
          </a>
        ) : null}
      </div>
      <div className="detail-grid">
        <section>
          <div className="outcome-panel">
            <h2>{t('pages.course.outcomeTitle')}</h2>
            <ul>
              <li>{t('pages.course.outcome1')}</li>
              <li>{t('pages.course.outcome2')}</li>
              <li>{t('pages.course.outcome3')}</li>
            </ul>
          </div>
          <div className="lesson-list">
            {lessons.map((lesson) => (
              <LessonRow key={lesson.slug} lesson={lesson} />
            ))}
          </div>
        </section>
        <aside className="side-panel course-side-panel">
          <div className="side-panel-stat">
            <span className="eyebrow">{t('pages.course.progressLabel')}</span>
            <strong>
              {publishedCount} / {lessons.length}
            </strong>
            <p>{t('pages.course.progressText')}</p>
          </div>
          <div className="side-panel-stat">
            <span className="eyebrow">{t('pages.course.timeLabel')}</span>
            <strong>
              {course.estimatedMinutes} {t('pages.common.minutesShort')}
            </strong>
            <p>{t('pages.course.timeText')}</p>
          </div>
          {firstLesson ? (
            <a
              className="primary-button"
              href={localizedPath('lesson', locale, {
                courseSlug: course.slug,
                lessonSlug: firstLesson.slug,
              })}
            >
              {t('pages.course.startFirstLesson')}
              <ArrowRight size={16} />
            </a>
          ) : null}
          <CourseCertificateCta courseSlug={course.slug} />
          <a className="ghost-button" href={localizedPath('courses', locale)}>
            {t('pages.common.allCourses')}
          </a>
          {course.tier === 'free' ? (
            <ProPathCard
              surface="course_pro_path"
              layout="compact"
              eyebrow={t('upsell.eyebrow')}
              title={t('upsell.courseTitle')}
              text={t('upsell.courseText')}
              tracking={{ course: course.slug }}
            />
          ) : null}
        </aside>
      </div>
    </main>
  )
}

/**
 * Renders a lesson or article section body: the lead paragraph plus any richer
 * blocks (bullet lists and code blocks). Sections that only have a `body` string
 * render exactly as before, so existing content is unaffected.
 */
export function LessonSectionBody({ section }: { section: LessonSection }) {
  return (
    <>
      <p>{section.body}</p>
      {section.blocks?.map((block, index) => {
        if (block.type === 'paragraph') {
          return <p key={index}>{block.text}</p>
        }
        if (block.type === 'bullets') {
          return (
            <ul key={index} className="lesson-section-list">
              {block.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )
        }
        return (
          <figure key={index} className="lesson-code-block">
            <pre>
              <code data-language={block.language}>{block.code}</code>
            </pre>
            {block.caption ? (
              <figcaption>{block.caption}</figcaption>
            ) : null}
          </figure>
        )
      })}
    </>
  )
}

/**
 * Periodic "still reading" heartbeat for the lesson page. While the tab is
 * visible it ticks once a minute; signed-in users post a 1-minute learning
 * heartbeat to Convex (server-throttled and per-day capped), anonymous users
 * record the minute into local activity so their streak/stats build up too.
 * Renders nothing.
 */
function LearningHeartbeat() {
  const { isSignedIn } = useAppAuth()
  const heartbeat = useMutation(api.interactions.learningHeartbeat)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const TICK_MS = 60_000

    function tick() {
      // Guard: never record a minute for a backgrounded tab. The interval is
      // paused while hidden, but keep this check so an in-flight tick at the
      // moment of hiding is still a no-op.
      if (document.visibilityState !== 'visible') return
      if (isSignedIn) {
        void heartbeat({ minutes: 1 }).catch(() => {})
        return
      }
      // Anonymous: fold a minute into local daily activity.
      try {
        const raw = window.localStorage.getItem(pendingStateStorageName)
        const state = parseAnonymousState(JSON.parse(raw ?? '{}'))
        const next = recordDailyActivity(state, utcDateKey(Date.now()), {
          minutesLearned: 1,
        })
        window.localStorage.setItem(
          pendingStateStorageName,
          JSON.stringify(next),
        )
      } catch {
        // ignore local write failures (private mode, quota)
      }
    }

    let interval: number | null = null
    function start() {
      if (interval === null) interval = window.setInterval(tick, TICK_MS)
    }
    function stop() {
      if (interval !== null) {
        window.clearInterval(interval)
        interval = null
      }
    }

    // Only run the timer while the tab is visible so a backgrounded tab does not
    // wake up once a minute; resume cleanly when the user returns.
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') start()
      else stop()
    }

    onVisibilityChange()
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      stop()
    }
  }, [isSignedIn, heartbeat])

  return null
}

export function LessonPage({
  courseSlug,
  lessonSlug,
}: {
  courseSlug: string
  lessonSlug: string
}) {
  const { locale, t } = useTranslation()
  const catalog = useCatalog()
  const course = catalog.getCourse(courseSlug)
  const lesson = catalog.getLesson(courseSlug, lessonSlug)
  if (!course || !lesson)
    return <NotFoundPage label={t('pages.notFound.lesson')} />
  const isPro = lesson.access === 'pro'
  // For Pro lessons `lesson.body` is ONLY the free preview; the Pro sections are
  // fetched from the entitlement-checked Convex query inside ProGatedContent.
  const lessonSections = lesson.body.map((section) => ({
    ...section,
    anchorId: slugify(section.title),
  }))
  // Sidebar outline: the preview sections, then (Pro) the locked titles, which
  // jump to the gated container until they are unlocked.
  const outline = [
    ...lessonSections.map((section) => ({
      title: section.title,
      href: `#${section.anchorId}`,
      locked: false,
    })),
    ...lesson.lockedSectionTitles.map((title) => ({
      title,
      href: `#${PRO_GATED_ID}`,
      locked: true,
    })),
  ]
  // Related Fundamentals for this lesson, resolved to the localized catalog so
  // titles render in the active language. Rendered as a link row right after
  // the Prerequisites section so beginners can fill a gap in place.
  const lessonFundamentals = lesson.relatedFundamentalSlugs
    .map((slug) => catalog.getFundamental(slug))
    .filter((item): item is Fundamental => Boolean(item))
  // The Prerequisites section is always the 3rd section of every lesson in
  // every locale (Summary, What you will learn, Prerequisites, ...), so match
  // by position instead of a per-language title list.
  const prereqIndex = 2
  const lessonPath = localizedPath('lesson', locale, {
    courseSlug: course.slug,
    lessonSlug: lesson.slug,
  })
  const renderSection = (
    section: LessonSection & { anchorId: string },
    index: number,
  ) => (
    <section
      className="lesson-section"
      id={section.anchorId}
      key={section.anchorId}
    >
      <h2>{section.title}</h2>
      <LessonSectionBody section={section} />
      {index === prereqIndex && lessonFundamentals.length ? (
        <div className="lesson-fundamentals" id="fundamentals">
          <strong className="lesson-fundamentals-label">
            {t('pages.lesson.fundamentalsHeading')}
          </strong>
          <div className="lesson-list">
            {lessonFundamentals.map((item) => (
              <a
                key={item.slug}
                className="lesson-row"
                href={localizedPath('fundamental', locale, {
                  fundamentalSlug: item.slug,
                })}
              >
                <span>{t('pages.lesson.fundamentalsTag')}</span>
                <div>
                  <strong>{item.title}</strong>
                  <p>{item.description}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  )
  const jsonLd = [
    buildLessonJsonLd({ lesson, courseTitle: course.title, locale }),
    buildBreadcrumbJsonLd([
      { name: t('pages.breadcrumbs.start'), path: localizedPath('home', locale) },
      {
        name: t('pages.breadcrumbs.courses'),
        path: localizedPath('courses', locale),
      },
      {
        name: course.title,
        path: localizedPath('course', locale, { courseSlug: course.slug }),
      },
      { name: lesson.title, path: lessonPath },
    ]),
    // HowTo steps would only describe the preview for a Pro lesson, so the
    // HowTo entity is emitted for fully readable (free) lessons only.
    ...(isPro
      ? []
      : [
          buildHowToJsonLd({
            name: lesson.title,
            description: lesson.summary,
            steps: lesson.body.map((section) => ({
              name: section.title,
              text: section.body,
            })),
            path: lessonPath,
            locale,
          }),
        ]),
  ]

  return (
    <main className="lesson-page">
      <LearningHeartbeat />
      <TrackOnMount
        name="lesson_started"
        props={{
          course: course.slug,
          lesson: lesson.slug,
          access: isPro ? 'pro' : 'free',
        }}
        dedupeKey={`${course.slug}/${lesson.slug}`}
      />
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <header className="lesson-header" data-hero-sentinel>
        <nav
          className="lesson-breadcrumbs"
          aria-label={t('common.breadcrumbLabel')}
        >
          <a href={localizedPath('courses', locale)}>
            {t('pages.breadcrumbs.courses')}
          </a>
          <ChevronRight size={12} aria-hidden="true" />
          <a
            href={localizedPath('course', locale, { courseSlug: course.slug })}
          >
            {course.title}
          </a>
          <ChevronRight size={12} aria-hidden="true" />
          <span>
            {t('pages.lesson.lessonPrefix')} {lesson.number}
          </span>
        </nav>
        <span className="lesson-eyebrow-row">
          <span className="eyebrow">
            {t('pages.lesson.lessonPrefix')} {lesson.number}
          </span>
          {isPro ? <ProBadge /> : null}
        </span>
        <h1>{lesson.title}</h1>
        <p>{lesson.subtitle}</p>
        <div className="lesson-meta-strip">
          <span>{lesson.duration}</span>
          <span>•</span>
          <span>{course.title}</span>
          <span>•</span>
          <span>
            {lesson.status === 'published'
              ? t('pages.common.available')
              : t('pages.common.roadmap')}
          </span>
        </div>
        <div className="lesson-actions">
          <ActionButton
            id={lesson.slug}
            kind="save"
            label={t('pages.lesson.save')}
          />
          <ActionButton
            id={lesson.slug}
            kind="like"
            label={t('pages.lesson.like')}
          />
          <ActionButton
            id={lesson.slug}
            kind="complete"
            label={t('pages.lesson.complete')}
          />
        </div>
      </header>
      <div className="lesson-layout lesson-detail-layout">
        <article className="lesson-content lesson-detail-content">
          <div className="lesson-intro-column">
            {lesson.youtubeId ? (
              <div className="video-box">
                <VideoConsentGate
                  title={lesson.title}
                  youtubeId={lesson.youtubeId}
                />
              </div>
            ) : null}
            <section className="outcome-panel">
              <h2>{t('pages.lesson.whatYouLearn')}</h2>
              <ul>
                {lesson.whatCovered.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          </div>
          <aside className="lesson-sidebar">
            <details className="lesson-sidebar-details" open>
              <summary>{t('pages.lesson.inThisLesson')}</summary>
              {outline.slice(0, isPro ? outline.length : 8).map((item) => (
                <a
                  key={item.title}
                  href={item.href}
                  className={item.locked ? 'is-locked' : undefined}
                >
                  {item.title}
                  {item.locked ? (
                    <Lock size={12} aria-label={t('pro.lockedSectionsLabel')} />
                  ) : null}
                </a>
              ))}
            </details>
          </aside>
          <div className="lesson-main-flow">
            {lessonSections.map(renderSection)}
            {isPro ? (
              <ProGatedContent
                locale={locale}
                courseSlug={course.slug}
                lessonSlug={lesson.slug}
                lockedSectionTitles={lesson.lockedSectionTitles}
                renderSections={(sections) =>
                  sections.map((section, index) =>
                    renderSection(
                      { ...section, anchorId: slugify(section.title) },
                      lessonSections.length + index,
                    ),
                  )
                }
              />
            ) : null}
            {/* A transcript only makes sense when there is a video to
                transcribe. Text-first lessons ARE the text, so rendering the
                derived stand-in would just duplicate the summary. */}
            {lesson.youtubeId ? (
              <section className="lesson-section">
                <h2>{t('pages.lesson.transcript')}</h2>
                <p>{lesson.transcript}</p>
              </section>
            ) : null}
            <LessonNextStep courseSlug={course.slug} lessonSlug={lesson.slug} />
            <CommentsSection
              targetTitle={lesson.title}
              targetType="lesson"
              targetId={lesson.slug}
            />
          </div>
        </article>
      </div>
      <div
        className="lesson-mobile-bar"
        aria-label={t('pages.lesson.actionsLabel')}
      >
        <ActionButton
          id={lesson.slug}
          kind="save"
          label={t('pages.lesson.save')}
        />
        <ActionButton
          id={lesson.slug}
          kind="complete"
          label={t('pages.lesson.done')}
        />
        <a className="state-button" href="#kommentare">
          {t('pages.lesson.comments')}
        </a>
        <a className="state-button" href="#next-step">
          {t('pages.lesson.next')}
        </a>
      </div>
    </main>
  )
}

function CommentsSection({
  targetTitle,
  targetType,
  targetId,
}: {
  targetTitle: string
  targetType: CommentTargetType
  targetId: string
}) {
  return (
    <CommentsErrorBoundary
      fallback={<CommentsFallback targetTitle={targetTitle} />}
    >
      <CommentsQuerySurface
        targetTitle={targetTitle}
        targetType={targetType}
        targetId={targetId}
      />
    </CommentsErrorBoundary>
  )
}

class CommentsErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: unknown) {
    // Swallowed by the fallback UI, so report it to error tracking explicitly.
    captureAnalyticsException(error, { boundary: 'comments' })
  }

  render() {
    if (this.state.hasError) return this.props.fallback
    return this.props.children
  }
}

function CommentsFallback({ targetTitle }: { targetTitle: string }) {
  const { locale, t } = useTranslation()
  return (
    <section className="comments-preview" id="kommentare">
      <div>
        <MessageSquareText size={22} />
        <h2>{t('pages.comments.heading')}</h2>
      </div>
      <p>
        {t('pages.comments.fallbackPrefix')}
        {targetTitle}
        {t('pages.comments.fallbackSuffix')}
      </p>
      <a className="ghost-button" href={localizedPath('register', locale)}>
        {t('pages.comments.submit')}
      </a>
    </section>
  )
}

function CommentsQuerySurface({
  targetTitle,
  targetType,
  targetId,
}: {
  targetTitle: string
  targetType: CommentTargetType
  targetId: string
}) {
  const { locale, t, formatDate } = useTranslation()
  const { isLoaded, isSignedIn } = useAppAuth()
  const comments = useQuery(api.comments.listForTarget, {
    targetType,
    targetId,
  })
  const createComment = useMutation(api.comments.create)
  const reportComment = useMutation(api.comments.report)
  const [status, setStatus] = useState<FormStatusState>('idle')
  const [reportStatus, setReportStatus] = useState<string | null>(null)

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setStatus('saving')
    try {
      await createComment({
        targetType,
        targetId,
        body: String(form.get('body') ?? ''),
      })
      setStatus('success')
      event.currentTarget.reset()
    } catch {
      setStatus('error')
    }
  }

  async function onReport(
    commentId: NonNullable<typeof comments>[number]['_id'],
  ) {
    setReportStatus(null)
    try {
      await reportComment({
        commentId,
        reason: t('pages.comments.reportReason'),
      })
      setReportStatus(t('pages.comments.reportThanks'))
    } catch {
      setReportStatus(t('pages.comments.reportNeedsAccount'))
    }
  }

  return (
    <section className="comments-preview" id="kommentare">
      <div>
        <MessageSquareText size={22} />
        <h2>{t('pages.comments.heading')}</h2>
      </div>
      {comments === undefined ? <p>{t('pages.comments.loading')}</p> : null}
      {comments?.length ? (
        <div className="comment-list">
          {comments.map((comment) => (
            <article className="comment-card" key={comment._id}>
              <div>
                <strong>{t('pages.comments.member')}</strong>
                <time dateTime={new Date(comment.createdAt).toISOString()}>
                  {formatDate(comment.createdAt)}
                </time>
              </div>
              <p>{comment.body}</p>
              {isSignedIn ? (
                <button
                  className="ghost-button compact"
                  type="button"
                  onClick={() => void onReport(comment._id)}
                >
                  {t('pages.comments.report')}
                </button>
              ) : null}
            </article>
          ))}
        </div>
      ) : comments !== undefined ? (
        <p>
          {t('pages.comments.emptyPrefix')}
          {targetTitle}
          {t('pages.comments.emptySuffix')}
        </p>
      ) : null}
      {isLoaded && isSignedIn ? (
        <form className="form-grid comment-form" onSubmit={onSubmit}>
          <textarea
            name="body"
            aria-label={t('pages.comments.commentLabel')}
            placeholder={t('pages.comments.placeholder')}
            required
          />
          <button className="primary-button" type="submit">
            {status === 'saving'
              ? t('pages.common.saving')
              : t('pages.comments.submit')}
          </button>
          <FormStatus
            status={status}
            success={t('pages.comments.published')}
          />
          {reportStatus ? (
            <p className="form-status" role="status">
              {reportStatus}
            </p>
          ) : null}
        </form>
      ) : (
        <a className="ghost-button" href={localizedPath('register', locale)}>
          {t('pages.comments.submit')}
        </a>
      )}
    </section>
  )
}

export function CommunityPage() {
  const { locale, t } = useTranslation()
  const catalog = useCatalog()
  return (
    <main className="page-shell">
      <PageIntro
        eyebrow={t('pages.community.eyebrow')}
        title={t('pages.community.title')}
        text={t('pages.community.text')}
        actions={
          <>
            <a
              className="primary-button"
              href={localizedPath('communityTopics', locale)}
            >
              {t('pages.community.allTopics')}
              <ArrowRight size={18} />
            </a>
            <a
              className="ghost-button"
              href={localizedPath('communityTopic', locale, {
                topicSlug: 'workflow-reviews',
              })}
            >
              {t('pages.community.workflowReviews')}
            </a>
          </>
        }
      />
      <div className="topic-grid">
        {catalog.communityTopics.map((topic) => (
          <a
            key={topic.slug}
            className="topic-card"
            href={localizedPath('communityTopic', locale, {
              topicSlug: topic.slug,
            })}
          >
            <h3>{topic.title}</h3>
            <p>{topic.description}</p>
          </a>
        ))}
      </div>
      <PostList posts={catalog.communityPosts} />
    </main>
  )
}

export function TopicsPage() {
  const { locale, t } = useTranslation()
  const catalog = useCatalog()
  return (
    <main className="page-shell">
      <PageIntro
        eyebrow={t('pages.community.topicsEyebrow')}
        title={t('pages.community.topicsTitle')}
        text={t('pages.community.topicsText')}
      />
      <div className="topic-grid">
        {catalog.communityTopics.map((topic) => (
          <a
            key={topic.slug}
            className="topic-card"
            href={localizedPath('communityTopic', locale, {
              topicSlug: topic.slug,
            })}
          >
            <h3>{topic.title}</h3>
            <p>{topic.description}</p>
          </a>
        ))}
      </div>
    </main>
  )
}

export function TopicPage({ topicSlug }: { topicSlug: string }) {
  const { t } = useTranslation()
  const catalog = useCatalog()
  const topic = catalog.communityTopics.find((item) => item.slug === topicSlug)
  const convexPosts = useQuery(
    api.community.listPostsForTopicSlug,
    topic ? { topicSlug: topic.slug } : 'skip',
  )
  if (!topic) return <NotFoundPage label={t('pages.notFound.topic')} />
  const posts = catalog.communityPosts.filter(
    (post) => post.topicSlug === topic.slug,
  )

  return (
    <main className="page-shell">
      <PageIntro
        eyebrow={t('pages.community.topicEyebrow')}
        title={topic.title}
        text={topic.description}
      />
      <PostList posts={posts} />
      <ConvexPostList posts={convexPosts ?? []} />
      <CommunityComposer topicSlug={topic.slug} />
    </main>
  )
}

function PostList({ posts }: { posts: CommunityPost[] }) {
  const { locale } = useTranslation()
  return (
    <div className="post-list">
      {posts.map((post) => (
        <a
          key={post.slug}
          className="post-row"
          href={localizedPath('communityPost', locale, { postSlug: post.slug })}
        >
          <span>{post.type.replace('_', ' ')}</span>
          <div>
            <strong>{post.title}</strong>
            <p>{post.excerpt}</p>
          </div>
        </a>
      ))}
    </div>
  )
}

function ConvexPostList({
  posts,
}: {
  posts: Array<{
    _id: string
    slug: string
    type: string
    title: string
    body: string
  }>
}) {
  const { locale } = useTranslation()
  if (!posts.length) return null
  return (
    <div className="post-list">
      {posts.map((post) => (
        <a
          key={post._id}
          className="post-row"
          href={localizedPath('communityPost', locale, { postSlug: post.slug })}
        >
          <span>{post.type.replace('_', ' ')}</span>
          <div>
            <strong>{post.title}</strong>
            <p>{post.body.slice(0, 140)}...</p>
          </div>
        </a>
      ))}
    </div>
  )
}

function CommunityComposer({ topicSlug }: { topicSlug: string }) {
  const { t } = useTranslation()
  const { isLoaded, isSignedIn } = useAppAuth()
  const createPost = useMutation(api.community.createPostForTopicSlug)
  const [status, setStatus] = useState<FormStatusState>('idle')

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setStatus('saving')
    try {
      await createPost({
        topicSlug,
        title: String(form.get('title') ?? ''),
        body: String(form.get('body') ?? ''),
        type: 'frage',
        tags: String(form.get('tags') ?? '')
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
      })
      setStatus('success')
      event.currentTarget.reset()
    } catch {
      setStatus('error')
    }
  }

  return (
    <section className="composer-panel">
      <h2>{t('pages.community.composerTitle')}</h2>
      {isLoaded && isSignedIn ? (
        <form className="form-grid" onSubmit={onSubmit}>
          <input
            name="title"
            aria-label={t('pages.community.composerTitleLabel')}
            placeholder={t('pages.community.composerTitlePlaceholder')}
            required
          />
          <textarea
            name="body"
            aria-label={t('pages.community.composerBodyLabel')}
            placeholder={t('pages.community.composerBodyPlaceholder')}
            required
          />
          <input
            name="tags"
            aria-label={t('pages.community.composerTagsLabel')}
            placeholder={t('pages.community.composerTagsPlaceholder')}
          />
          <button className="primary-button" type="submit">
            {status === 'saving'
              ? t('pages.common.saving')
              : t('pages.community.composerSubmit')}
          </button>
          <FormStatus
            status={status}
            success={t('pages.community.composerPublished')}
          />
        </form>
      ) : (
        <p>{t('pages.community.composerLocked')}</p>
      )}
    </section>
  )
}

export function CommunityPostPage({ postSlug }: { postSlug: string }) {
  const { locale, t } = useTranslation()
  const post = useCatalog().getCommunityPost(postSlug)
  const convexPost = useQuery(
    api.community.getPostBySlug,
    post ? 'skip' : { postSlug },
  )
  if (!post && convexPost === undefined) {
    return (
      <UtilityPage
        eyebrow={t('pages.community.eyebrow')}
        title={t('pages.community.postLoadingTitle')}
        text={t('pages.community.postLoadingText')}
      />
    )
  }
  if (!post && !convexPost)
    return <NotFoundPage label={t('pages.notFound.post')} />
  const title = post?.title ?? convexPost!.title
  const excerpt = post?.excerpt ?? convexPost!.body.slice(0, 160)
  const body = post?.body ?? convexPost!.body
  const jsonLd = post
    ? [
        buildBlogPostingJsonLd({
          title: post.title,
          description: post.excerpt,
          body: post.body,
          datePublished: post.createdAt,
          path: localizedPath('communityPost', locale, { postSlug: post.slug }),
          keywords: post.tags.join(', '),
          locale,
        }),
        buildBreadcrumbJsonLd([
          { name: t('pages.breadcrumbs.start'), path: localizedPath('home', locale) },
          {
            name: t('pages.breadcrumbs.community'),
            path: localizedPath('community', locale),
          },
          {
            name: post.title,
            path: localizedPath('communityPost', locale, {
              postSlug: post.slug,
            }),
          },
        ]),
      ]
    : []

  return (
    <main className="page-shell narrow">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <PageIntro
        eyebrow={t('pages.community.postEyebrow')}
        title={title}
        text={excerpt}
      />
      <article className="editorial-block">
        <p>{body}</p>
      </article>
      <CommentsSection
        targetTitle={title}
        targetType="post"
        targetId={postSlug}
      />
    </main>
  )
}

export function ResourcesPage() {
  const { locale, t, dict } = useTranslation()
  const catalog = useCatalog()
  const typeLabels = dict.pages.resources.typeLabels
  return (
    <main className="page-shell">
      <PageIntro
        eyebrow={t('pages.resources.eyebrow')}
        title={t('pages.resources.title')}
        text={t('pages.resources.text')}
        actions={
          <>
            <a className="primary-button" href={localizedPath('courses', locale)}>
              {t('pages.common.toCourses')}
              <ArrowRight size={18} />
            </a>
            <a
              className="ghost-button"
              href={localizedPath('changelog', locale)}
            >
              {t('pages.resources.viewChangelog')}
            </a>
          </>
        }
      />
      <div className="resource-grid">
        {catalog.resources.map((resource) => (
          <a
            key={resource.slug}
            className={`resource-card resource-card-${resource.type}`}
            href={localizedPath('resource', locale, {
              resourceSlug: resource.slug,
            })}
          >
            <span className="resource-card-icon" aria-hidden="true">
              <ResourceIcon type={resource.type} />
            </span>
            <h3>{resource.title}</h3>
            <p>{resource.shortDescription}</p>
            <small>{typeLabels[resource.type]}</small>
          </a>
        ))}
      </div>
    </main>
  )
}

function ResourceIcon({ type }: { type: Resource['type'] }) {
  switch (type) {
    case 'tool':
      return <Wrench size={20} />
    case 'api':
      return <PlugZap size={20} />
    case 'prompt':
      return <Lightbulb size={20} />
    case 'skill':
      return <Code2 size={20} />
    case 'template':
      return <FileText size={20} />
    case 'checklist':
      return <CheckSquare size={20} />
    case 'guide':
    default:
      return <BookOpen size={20} />
  }
}

export function ResourcePage({ resourceSlug }: { resourceSlug: string }) {
  const { locale, t } = useTranslation()
  const resource = useCatalog().getResource(resourceSlug)
  if (!resource) return <NotFoundPage label={t('pages.notFound.resource')} />
  const resourceSections = [
    t('pages.resources.sectionSummary'),
    t('pages.resources.sectionGoodFor'),
    t('pages.resources.sectionWhen'),
    t('pages.resources.sectionSetup'),
    t('pages.resources.sectionRisks'),
  ].map((title) => ({
    anchorId: slugify(title),
    body: resource.body,
    title,
  }))
  const jsonLd = [
    buildResourceJsonLd({
      resource,
      path: localizedPath('resource', locale, { resourceSlug: resource.slug }),
      locale,
    }),
    buildBreadcrumbJsonLd([
      { name: t('pages.breadcrumbs.start'), path: localizedPath('home', locale) },
      {
        name: t('pages.breadcrumbs.resources'),
        path: localizedPath('resources', locale),
      },
      {
        name: resource.title,
        path: localizedPath('resource', locale, {
          resourceSlug: resource.slug,
        }),
      },
    ]),
  ]

  return (
    <main className="page-shell">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <PageIntro
        eyebrow={resource.type}
        title={resource.title}
        text={resource.shortDescription}
      />
      <div className="lesson-layout lesson-detail-layout">
        <article className="lesson-content lesson-detail-content">
          <section
            className="outcome-panel resource-summary-panel"
            id={resourceSections[0].anchorId}
          >
            <h2>{resourceSections[0].title}</h2>
            <p>{resourceSections[0].body}</p>
          </section>
          <aside className="lesson-sidebar resource-sidebar">
            <details className="lesson-sidebar-details" open>
              <summary>{t('pages.resources.inThisResource')}</summary>
              {resourceSections.map((section) => (
                <a key={section.title} href={`#${section.anchorId}`}>
                  {section.title}
                </a>
              ))}
            </details>
          </aside>
          <div className="lesson-main-flow">
            {resourceSections.slice(1).map((section) => (
              <section
                className="lesson-section"
                id={section.anchorId}
                key={section.title}
              >
                <h2>{section.title}</h2>
                <p>{section.body}</p>
              </section>
            ))}
          </div>
        </article>
      </div>
    </main>
  )
}

export function WissenIndexPage() {
  const { locale, t, dict } = useTranslation()
  const catalog = useCatalog()
  const categoryLabels = dict.pages.knowledge.categoryLabels
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      ...buildItemListJsonLd(
        catalog.articles.map((article) => ({
          name: article.title,
          path: localizedPath('article', locale, {
            articleSlug: article.slug,
          }),
        })),
      ),
    },
    buildBreadcrumbJsonLd([
      { name: t('pages.breadcrumbs.start'), path: localizedPath('home', locale) },
      {
        name: t('pages.breadcrumbs.knowledge'),
        path: localizedPath('knowledge', locale),
      },
    ]),
  ]

  return (
    <main className="page-shell">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <PageIntro
        eyebrow={t('pages.knowledge.eyebrow')}
        title={t('pages.knowledge.title')}
        text={t('pages.knowledge.text')}
        actions={
          <>
            <a className="primary-button" href={localizedPath('courses', locale)}>
              {t('pages.common.toCourses')}
              <ArrowRight size={18} />
            </a>
            <a
              className="ghost-button"
              href={localizedPath('resources', locale)}
            >
              {t('pages.common.viewResources')}
            </a>
          </>
        }
      />
      <div className="resource-grid">
        {catalog.articles.map((article) => (
          <a
            key={article.slug}
            className="resource-card resource-card-guide"
            href={localizedPath('article', locale, {
              articleSlug: article.slug,
            })}
          >
            <span className="resource-card-icon" aria-hidden="true">
              <BookOpen size={20} />
            </span>
            <h3>{article.title}</h3>
            <p>{article.description}</p>
            <small>
              {categoryLabels[article.category]} · {article.readingMinutes}{' '}
              {t('pages.common.readingMinutes')}
            </small>
          </a>
        ))}
      </div>
    </main>
  )
}

export function WissenArticlePage({ articleSlug }: { articleSlug: string }) {
  const { locale, t, dict, formatDate } = useTranslation()
  const catalog = useCatalog()
  const categoryLabels = dict.pages.knowledge.categoryLabels
  const article = catalog.getArticle(articleSlug)
  if (!article) return <NotFoundPage label={t('pages.notFound.article')} />

  const relatedLessons = article.relatedLessonSlugs
    .map((slug) => catalog.lessons.find((lesson) => lesson.slug === slug))
    .filter((lesson): lesson is Lesson => Boolean(lesson))
  const relatedResources = article.relatedResourceSlugs
    .map((slug) => catalog.getResource(slug))
    .filter((resource): resource is Resource => Boolean(resource))
  const articleSections = article.sections.map((section) => ({
    ...section,
    anchorId: slugify(section.heading),
  }))

  const articleBody = [
    article.intro,
    ...articleSections.map((section) => `${section.heading}. ${section.body}`),
  ].join('\n\n')

  const jsonLd = [
    {
      ...buildBlogPostingJsonLd({
        title: article.title,
        description: article.description,
        body: articleBody,
        datePublished: article.datePublished,
        path: localizedPath('article', locale, { articleSlug: article.slug }),
        keywords: article.keywords.join(', '),
        locale,
      }),
      dateModified: article.dateModified,
    },
    buildFaqJsonLd(article.faq, locale),
    buildBreadcrumbJsonLd([
      { name: t('pages.breadcrumbs.start'), path: localizedPath('home', locale) },
      {
        name: t('pages.breadcrumbs.knowledge'),
        path: localizedPath('knowledge', locale),
      },
      {
        name: article.title,
        path: localizedPath('article', locale, { articleSlug: article.slug }),
      },
    ]),
  ]

  return (
    <main className="lesson-page">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <header className="lesson-header" data-hero-sentinel>
        <nav
          className="lesson-breadcrumbs"
          aria-label={t('common.breadcrumbLabel')}
        >
          <a href={localizedPath('knowledge', locale)}>
            {t('pages.breadcrumbs.knowledge')}
          </a>
          <ChevronRight size={12} aria-hidden="true" />
          <span>{categoryLabels[article.category]}</span>
        </nav>
        <span className="eyebrow">{article.heroEyebrow}</span>
        <h1>{article.title}</h1>
        <div className="lesson-meta-strip">
          <span>{categoryLabels[article.category]}</span>
          <span>•</span>
          <span>
            {article.readingMinutes} {t('pages.common.readingMinutes')}
          </span>
          <span>•</span>
          <span>{formatDate(article.dateModified)}</span>
        </div>
      </header>
      <div className="lesson-layout lesson-detail-layout">
        <article className="lesson-content lesson-detail-content">
          <section className="outcome-panel article-summary-panel">
            <h2>{t('pages.knowledge.summary')}</h2>
            <p className="resource-intro">{article.intro}</p>
          </section>
          <aside className="lesson-sidebar article-sidebar">
            <details className="lesson-sidebar-details" open>
              <summary>{t('pages.knowledge.inThisArticle')}</summary>
              {articleSections.map((section) => (
                <a key={section.heading} href={`#${section.anchorId}`}>
                  {section.heading}
                </a>
              ))}
            </details>
          </aside>
          <div className="lesson-main-flow">
            {articleSections.map((section) => (
              <section
                className="lesson-section"
                id={section.anchorId}
                key={section.heading}
              >
                <h2>{section.heading}</h2>
                <p>{section.body}</p>
              </section>
            ))}
            <section className="lesson-section" id="faq">
              <h2>{t('pages.knowledge.faqHeading')}</h2>
              <FaqAccordion items={article.faq} variant="article" />
            </section>
            {relatedLessons.length || relatedResources.length ? (
              <section className="lesson-section" id="passende-inhalte">
                <h2>{t('pages.knowledge.relatedHeading')}</h2>
                <div className="lesson-list">
                  {relatedLessons.map((lesson) => (
                    <a
                      key={lesson.slug}
                      className="lesson-row"
                      href={localizedPath('lesson', locale, {
                        courseSlug: lesson.courseSlug,
                        lessonSlug: lesson.slug,
                      })}
                    >
                      <span>{t('pages.common.lesson')}</span>
                      <div>
                        <strong>{lesson.title}</strong>
                        <p>{lesson.outcome}</p>
                      </div>
                      <small>{t('pages.common.open')}</small>
                    </a>
                  ))}
                  {relatedResources.map((resource) => (
                    <a
                      key={resource.slug}
                      className="lesson-row"
                      href={localizedPath('resource', locale, {
                        resourceSlug: resource.slug,
                      })}
                    >
                      <span>{t('pages.common.resource')}</span>
                      <div>
                        <strong>{resource.title}</strong>
                        <p>{resource.shortDescription}</p>
                      </div>
                      <small>{t('pages.common.open')}</small>
                    </a>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </article>
      </div>
    </main>
  )
}

// Order categories deterministically on the index page.
const FUNDAMENTAL_CATEGORY_ORDER: FundamentalCategory[] = [
  'concepts',
  'tools',
  'workflow',
]


export function FundamentalsIndexPage() {
  const { locale, dict } = useTranslation()
  const catalog = useCatalog()
  const ui = dict.pages.fundamentals
  const items = catalog.fundamentals

  const grouped = FUNDAMENTAL_CATEGORY_ORDER.map((category) => ({
    category,
    items: items.filter((item) => item.category === category),
  })).filter((group) => group.items.length > 0)

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      ...buildItemListJsonLd(
        items.map((item) => ({
          name: item.title,
          path: localizedPath('fundamental', locale, {
            fundamentalSlug: item.slug,
          }),
        })),
      ),
    },
    buildBreadcrumbJsonLd([
      { name: dict.pages.breadcrumbs.start, path: localizedPath('home', locale) },
      { name: ui.breadcrumb, path: localizedPath('fundamentals', locale) },
    ]),
  ]

  return (
    <main className="page-shell">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <PageIntro
        eyebrow={ui.eyebrow}
        title={ui.title}
        text={ui.intro}
        actions={
          <>
            <a className="primary-button" href={localizedPath('courses', locale)}>
              {ui.toCourses}
              <ArrowRight size={18} />
            </a>
            <a
              className="ghost-button"
              href={localizedPath('knowledge', locale)}
            >
              {ui.toKnowledge}
            </a>
          </>
        }
      />
      {grouped.map((group) => (
        <section className="fundamentals-group" key={group.category}>
          <h2 className="fundamentals-group-title">
            {ui.categories[group.category]}
          </h2>
          <div className="resource-grid">
            {group.items.map((item) => (
              <a
                key={item.slug}
                className="resource-card resource-card-guide"
                href={localizedPath('fundamental', locale, {
                  fundamentalSlug: item.slug,
                })}
              >
                <span className="resource-card-icon" aria-hidden="true">
                  <Compass size={20} />
                </span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <small>
                  {ui.categories[item.category]} ·{' '}
                  {fundamentalReadingMinutes(item)} {ui.minRead}
                </small>
              </a>
            ))}
          </div>
        </section>
      ))}
    </main>
  )
}

export function FundamentalPage({
  fundamentalSlug,
}: {
  fundamentalSlug: string
}) {
  const { locale, dict, formatDate } = useTranslation()
  const catalog = useCatalog()
  const ui = dict.pages.fundamentals
  const fundamental = catalog.getFundamental(fundamentalSlug)
  if (!fundamental) return <NotFoundPage label={ui.notFound} />

  const relatedFundamentals = fundamental.relatedFundamentalSlugs
    .map((slug) => catalog.getFundamental(slug))
    .filter((item): item is Fundamental => Boolean(item))
  const relatedLessons = fundamental.relatedLessonSlugs
    .map((slug) => catalog.lessons.find((lesson) => lesson.slug === slug))
    .filter((lesson): lesson is Lesson => Boolean(lesson))

  const sections = fundamental.sections.map((section) => ({
    ...section,
    anchorId: slugify(section.title),
  }))

  const fundamentalPath = localizedPath('fundamental', locale, {
    fundamentalSlug: fundamental.slug,
  })

  const bodyText = [
    fundamental.intro,
    ...sections.map((section) => `${section.title}. ${section.body}`),
  ].join('\n\n')

  const jsonLd = [
    {
      ...buildBlogPostingJsonLd({
        title: fundamental.title,
        description: fundamental.description,
        body: bodyText,
        datePublished: fundamental.updatedAt,
        path: fundamentalPath,
        keywords: fundamental.keywords.join(', '),
        locale,
      }),
      dateModified: fundamental.updatedAt,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'DefinedTerm',
      name: fundamental.title,
      description: fundamental.intro,
      inLanguage: LOCALE_HTML_LANG[locale],
      url: localizedPath('fundamental', locale, {
        fundamentalSlug: fundamental.slug,
      }),
    },
    buildFaqJsonLd(fundamental.faq, locale),
    buildBreadcrumbJsonLd([
      { name: dict.pages.breadcrumbs.start, path: localizedPath('home', locale) },
      { name: ui.breadcrumb, path: localizedPath('fundamentals', locale) },
      { name: fundamental.title, path: fundamentalPath },
    ]),
  ]

  return (
    <main className="lesson-page">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <header className="lesson-header" data-hero-sentinel>
        <nav
          className="lesson-breadcrumbs"
          aria-label={dict.common.breadcrumbLabel}
        >
          <a href={localizedPath('fundamentals', locale)}>{ui.breadcrumb}</a>
          <ChevronRight size={12} aria-hidden="true" />
          <span>{ui.categories[fundamental.category]}</span>
        </nav>
        <span className="eyebrow">{ui.eyebrow}</span>
        <h1>{fundamental.title}</h1>
        <div className="lesson-meta-strip">
          <span>{ui.categories[fundamental.category]}</span>
          <span>•</span>
          <span>
            {fundamentalReadingMinutes(fundamental)} {ui.minRead}
          </span>
          <span>•</span>
          <span>
            {ui.updated} {formatDate(fundamental.updatedAt)}
          </span>
        </div>
      </header>
      <div className="lesson-layout lesson-detail-layout">
        <article className="lesson-content lesson-detail-content">
          <section className="outcome-panel article-summary-panel">
            <h2>{ui.definition}</h2>
            <p className="resource-intro">{fundamental.intro}</p>
          </section>
          <aside className="lesson-sidebar article-sidebar">
            <details className="lesson-sidebar-details" open>
              <summary>{ui.inThisPage}</summary>
              {sections.map((section) => (
                <a key={section.title} href={`#${section.anchorId}`}>
                  {section.title}
                </a>
              ))}
              <a href="#faq">{ui.faqHeading}</a>
            </details>
          </aside>
          <div className="lesson-main-flow">
            {sections.map((section) => (
              <section
                className="lesson-section"
                id={section.anchorId}
                key={section.title}
              >
                <h2>{section.title}</h2>
                <LessonSectionBody section={section} />
              </section>
            ))}
            <section className="lesson-section" id="faq">
              <h2>{ui.faqHeading}</h2>
              <FaqAccordion items={fundamental.faq} variant="article" />
            </section>
            {relatedFundamentals.length || relatedLessons.length ? (
              <section className="lesson-section" id="related">
                <h2>{ui.relatedHeading}</h2>
                <div className="lesson-list">
                  {relatedFundamentals.map((item) => (
                    <a
                      key={item.slug}
                      className="lesson-row"
                      href={localizedPath('fundamental', locale, {
                        fundamentalSlug: item.slug,
                      })}
                    >
                      <span>{ui.fundamentalTag}</span>
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.description}</p>
                      </div>
                      <small>{ui.open}</small>
                    </a>
                  ))}
                  {relatedLessons.map((lesson) => (
                    <a
                      key={lesson.slug}
                      className="lesson-row"
                      href={localizedPath('lesson', locale, {
                        courseSlug: lesson.courseSlug,
                        lessonSlug: lesson.slug,
                      })}
                    >
                      <span>{ui.lessonTag}</span>
                      <div>
                        <strong>{lesson.title}</strong>
                        <p>{lesson.outcome}</p>
                      </div>
                      <small>{ui.open}</small>
                    </a>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </article>
      </div>
    </main>
  )
}

// ---------------------------------------------------------------------------
// Glossary: AI / agent vocabulary
// ---------------------------------------------------------------------------


export function GlossaryIndexPage() {
  const { locale, dict } = useTranslation()
  const catalog = useCatalog()
  const ui = dict.pages.glossary
  const items = [...catalog.glossaryTerms].sort((a, b) =>
    a.term.localeCompare(b.term, locale === 'de' ? 'de' : 'en'),
  )

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      ...buildItemListJsonLd(
        items.map((item) => ({
          name: item.term,
          path: localizedPath('glossaryTerm', locale, { termSlug: item.slug }),
        })),
      ),
    },
    buildBreadcrumbJsonLd([
      { name: dict.pages.breadcrumbs.start, path: localizedPath('home', locale) },
      { name: ui.breadcrumb, path: localizedPath('glossary', locale) },
    ]),
  ]

  return (
    <main className="page-shell">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <PageIntro
        eyebrow={ui.eyebrow}
        title={ui.title}
        text={ui.intro}
        actions={
          <>
            <a className="primary-button" href={localizedPath('guides', locale)}>
              {ui.toGuides}
              <ArrowRight size={18} />
            </a>
            <a
              className="ghost-button"
              href={localizedPath('fundamentals', locale)}
            >
              {ui.toFundamentals}
            </a>
          </>
        }
      />
      <section className="fundamentals-group">
        <div className="resource-grid">
          {items.map((item) => (
            <a
              key={item.slug}
              className="resource-card resource-card-guide"
              href={localizedPath('glossaryTerm', locale, {
                termSlug: item.slug,
              })}
            >
              <span className="resource-card-icon" aria-hidden="true">
                <BookOpen size={20} />
              </span>
              <h3>{item.term}</h3>
              <p>{item.description}</p>
              <small>
                {ui.termTag} · {glossaryReadingMinutes(item)} {ui.minRead}
              </small>
            </a>
          ))}
        </div>
      </section>
    </main>
  )
}

export function GlossaryTermPage({ termSlug }: { termSlug: string }) {
  const { locale, dict, formatDate } = useTranslation()
  const catalog = useCatalog()
  const ui = dict.pages.glossary
  const term = catalog.getGlossaryTerm(termSlug)
  if (!term) return <NotFoundPage label={ui.notFound} />

  const relatedTerms = term.relatedTermSlugs
    .map((slug) => catalog.getGlossaryTerm(slug))
    .filter((item): item is GlossaryTerm => Boolean(item))
  const relatedGuides = term.relatedGuideSlugs
    .map((slug) => catalog.getGuide(slug))
    .filter((item): item is Guide => Boolean(item))
  const relatedFundamentals = term.relatedFundamentalSlugs
    .map((slug) => catalog.getFundamental(slug))
    .filter((item): item is Fundamental => Boolean(item))

  const sections = term.sections.map((section) => ({
    ...section,
    anchorId: slugify(section.title),
  }))

  const termPath = localizedPath('glossaryTerm', locale, { termSlug: term.slug })

  const bodyText = [
    term.intro,
    ...sections.map((section) => `${section.title}. ${section.body}`),
  ].join('\n\n')

  const jsonLd = [
    {
      ...buildBlogPostingJsonLd({
        title: term.title,
        description: term.description,
        body: bodyText,
        datePublished: term.updatedAt,
        path: termPath,
        keywords: term.keywords.join(', '),
        locale,
      }),
      dateModified: term.updatedAt,
    },
    buildDefinedTermJsonLd({
      name: term.term,
      description: term.intro,
      path: termPath,
      locale,
    }),
    buildFaqJsonLd(term.faq, locale),
    buildBreadcrumbJsonLd([
      { name: dict.pages.breadcrumbs.start, path: localizedPath('home', locale) },
      { name: ui.breadcrumb, path: localizedPath('glossary', locale) },
      { name: term.term, path: termPath },
    ]),
  ]

  return (
    <main className="lesson-page">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <header className="lesson-header" data-hero-sentinel>
        <nav
          className="lesson-breadcrumbs"
          aria-label={dict.common.breadcrumbLabel}
        >
          <a href={localizedPath('glossary', locale)}>{ui.breadcrumb}</a>
          <ChevronRight size={12} aria-hidden="true" />
          <span>{term.term}</span>
        </nav>
        <span className="eyebrow">{ui.eyebrow}</span>
        <h1>{term.title}</h1>
        <div className="lesson-meta-strip">
          <span>{ui.termTag}</span>
          <span>•</span>
          <span>
            {glossaryReadingMinutes(term)} {ui.minRead}
          </span>
          <span>•</span>
          <span>
            {ui.updated} {formatDate(term.updatedAt)}
          </span>
        </div>
      </header>
      <div className="lesson-layout lesson-detail-layout">
        <article className="lesson-content lesson-detail-content">
          <section className="outcome-panel article-summary-panel">
            <h2>{ui.definition}</h2>
            <p className="resource-intro">{term.intro}</p>
          </section>
          <aside className="lesson-sidebar article-sidebar">
            <details className="lesson-sidebar-details" open>
              <summary>{ui.inThisPage}</summary>
              {sections.map((section) => (
                <a key={section.title} href={`#${section.anchorId}`}>
                  {section.title}
                </a>
              ))}
              <a href="#faq">{ui.faqHeading}</a>
            </details>
          </aside>
          <div className="lesson-main-flow">
            {sections.map((section) => (
              <section
                className="lesson-section"
                id={section.anchorId}
                key={section.title}
              >
                <h2>{section.title}</h2>
                <LessonSectionBody section={section} />
              </section>
            ))}
            <section className="lesson-section" id="faq">
              <h2>{ui.faqHeading}</h2>
              <FaqAccordion items={term.faq} variant="article" />
            </section>
            {relatedTerms.length ||
            relatedGuides.length ||
            relatedFundamentals.length ? (
              <section className="lesson-section" id="related">
                <h2>{ui.relatedHeading}</h2>
                <div className="lesson-list">
                  {relatedTerms.map((item) => (
                    <a
                      key={item.slug}
                      className="lesson-row"
                      href={localizedPath('glossaryTerm', locale, {
                        termSlug: item.slug,
                      })}
                    >
                      <span>{ui.termTag}</span>
                      <div>
                        <strong>{item.term}</strong>
                        <p>{item.description}</p>
                      </div>
                      <small>{ui.open}</small>
                    </a>
                  ))}
                  {relatedGuides.map((item) => (
                    <a
                      key={item.slug}
                      className="lesson-row"
                      href={localizedPath('guide', locale, {
                        guideSlug: item.slug,
                      })}
                    >
                      <span>{ui.guideTag}</span>
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.description}</p>
                      </div>
                      <small>{ui.open}</small>
                    </a>
                  ))}
                  {relatedFundamentals.map((item) => (
                    <a
                      key={item.slug}
                      className="lesson-row"
                      href={localizedPath('fundamental', locale, {
                        fundamentalSlug: item.slug,
                      })}
                    >
                      <span>{ui.fundamentalTag}</span>
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.description}</p>
                      </div>
                      <small>{ui.open}</small>
                    </a>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </article>
      </div>
    </main>
  )
}

// ---------------------------------------------------------------------------
// Compare: structured "X vs Y" comparisons
// ---------------------------------------------------------------------------


export function CompareIndexPage() {
  const { locale, dict } = useTranslation()
  const catalog = useCatalog()
  const ui = dict.pages.compare
  const items = catalog.comparisons

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      ...buildItemListJsonLd(
        items.map((item) => ({
          name: item.title,
          path: localizedPath('comparison', locale, {
            comparisonSlug: item.slug,
          }),
        })),
      ),
    },
    buildBreadcrumbJsonLd([
      { name: dict.pages.breadcrumbs.start, path: localizedPath('home', locale) },
      { name: ui.breadcrumb, path: localizedPath('compare', locale) },
    ]),
  ]

  return (
    <main className="page-shell">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <PageIntro
        eyebrow={ui.eyebrow}
        title={ui.title}
        text={ui.intro}
        actions={
          <>
            <a className="primary-button" href={localizedPath('guides', locale)}>
              {ui.toGuides}
              <ArrowRight size={18} />
            </a>
            <a
              className="ghost-button"
              href={localizedPath('glossary', locale)}
            >
              {ui.toGlossary}
            </a>
          </>
        }
      />
      <section className="fundamentals-group">
        <div className="resource-grid">
          {items.map((item) => (
            <a
              key={item.slug}
              className="resource-card resource-card-guide"
              href={localizedPath('comparison', locale, {
                comparisonSlug: item.slug,
              })}
            >
              <span className="resource-card-icon" aria-hidden="true">
                <Scale size={20} />
              </span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <small>
                {ui.comparisonTag} · {comparisonReadingMinutes(item)}{' '}
                {ui.minRead}
              </small>
            </a>
          ))}
        </div>
      </section>
    </main>
  )
}

export function ComparisonPage({
  comparisonSlug,
}: {
  comparisonSlug: string
}) {
  const { locale, dict, formatDate } = useTranslation()
  const catalog = useCatalog()
  const ui = dict.pages.compare
  const comparison = catalog.getComparison(comparisonSlug)
  if (!comparison) return <NotFoundPage label={ui.notFound} />

  const relatedGuides = comparison.relatedGuideSlugs
    .map((slug) => catalog.getGuide(slug))
    .filter((item): item is Guide => Boolean(item))
  const relatedComparisons = comparison.relatedComparisonSlugs
    .map((slug) => catalog.getComparison(slug))
    .filter((item): item is Comparison => Boolean(item))
  const relatedTerms = comparison.relatedTermSlugs
    .map((slug) => catalog.getGlossaryTerm(slug))
    .filter((item): item is GlossaryTerm => Boolean(item))

  const comparisonPath = localizedPath('comparison', locale, {
    comparisonSlug: comparison.slug,
  })

  const bodyText = [
    comparison.intro,
    ...comparison.options.map(
      (option) =>
        `${option.name}. ${option.tagline} ${option.bestFor} ${option.pros.join(
          ' ',
        )} ${option.cons.join(' ')}`,
    ),
    comparison.verdict,
  ].join('\n\n')

  const jsonLd = [
    {
      ...buildBlogPostingJsonLd({
        title: comparison.title,
        description: comparison.description,
        body: bodyText,
        datePublished: comparison.datePublished,
        path: comparisonPath,
        keywords: comparison.keywords.join(', '),
        locale,
      }),
      dateModified: comparison.dateModified,
    },
    {
      '@context': 'https://schema.org',
      ...buildItemListJsonLd(
        comparison.options.map((option) => ({
          name: option.name,
          path: comparisonPath,
        })),
      ),
    },
    buildFaqJsonLd(comparison.faq, locale),
    buildBreadcrumbJsonLd([
      { name: dict.pages.breadcrumbs.start, path: localizedPath('home', locale) },
      { name: ui.breadcrumb, path: localizedPath('compare', locale) },
      { name: comparison.title, path: comparisonPath },
    ]),
  ]

  return (
    <main className="lesson-page">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <header className="lesson-header" data-hero-sentinel>
        <nav
          className="lesson-breadcrumbs"
          aria-label={dict.common.breadcrumbLabel}
        >
          <a href={localizedPath('compare', locale)}>{ui.breadcrumb}</a>
          <ChevronRight size={12} aria-hidden="true" />
          <span>{comparison.title}</span>
        </nav>
        <span className="eyebrow">{ui.eyebrow}</span>
        <h1>{comparison.title}</h1>
        <div className="lesson-meta-strip">
          <span>{ui.comparisonTag}</span>
          <span>•</span>
          <span>
            {comparisonReadingMinutes(comparison)} {ui.minRead}
          </span>
          <span>•</span>
          <span>
            {ui.updated} {formatDate(comparison.dateModified)}
          </span>
        </div>
      </header>
      <div className="lesson-layout lesson-detail-layout">
        <article className="lesson-content lesson-detail-content">
          <section className="outcome-panel article-summary-panel">
            <h2>{ui.atAGlance}</h2>
            <p className="resource-intro">{comparison.intro}</p>
          </section>
          <aside className="lesson-sidebar article-sidebar">
            <details className="lesson-sidebar-details" open>
              <summary>{ui.inThisPage}</summary>
              <a href="#options">{ui.optionsHeading}</a>
              <a href="#spec">{ui.specHeading}</a>
              <a href="#verdict">{ui.verdictHeading}</a>
              <a href="#faq">{ui.faqHeading}</a>
            </details>
          </aside>
          <div className="lesson-main-flow">
            <section className="lesson-section" id="options">
              <h2>{ui.optionsHeading}</h2>
              <div className="compare-options">
                {comparison.options.map((option) => (
                  <div className="compare-option-card" key={option.key}>
                    <h3>{option.name}</h3>
                    <p className="compare-option-tagline">{option.tagline}</p>
                    <p className="compare-option-bestfor">
                      <strong>{ui.bestForLabel}:</strong> {option.bestFor}
                    </p>
                    <div className="compare-option-lists">
                      <div>
                        <h4>{ui.prosLabel}</h4>
                        <ul className="lesson-section-list">
                          {option.pros.map((pro) => (
                            <li key={pro}>{pro}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4>{ui.consLabel}</h4>
                        <ul className="lesson-section-list">
                          {option.cons.map((con) => (
                            <li key={con}>{con}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
            <section className="lesson-section" id="spec">
              <h2>{ui.specHeading}</h2>
              <div className="compare-table-wrap">
                <table className="compare-table">
                  <thead>
                    <tr>
                      <th scope="col">{ui.dimensionLabel}</th>
                      {comparison.options.map((option) => (
                        <th scope="col" key={option.key}>
                          {option.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {comparison.dimensions.map((dimension) => (
                      <tr key={dimension.label}>
                        <th scope="row">{dimension.label}</th>
                        {comparison.options.map((option) => (
                          <td key={option.key}>
                            {dimension.values[option.key] ?? '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="lesson-section" id="verdict">
              <h2>{ui.verdictHeading}</h2>
              <p>{comparison.verdict}</p>
            </section>
            <section className="lesson-section" id="faq">
              <h2>{ui.faqHeading}</h2>
              <FaqAccordion items={comparison.faq} variant="article" />
            </section>
            {relatedGuides.length ||
            relatedComparisons.length ||
            relatedTerms.length ? (
              <section className="lesson-section" id="related">
                <h2>{ui.relatedHeading}</h2>
                <div className="lesson-list">
                  {relatedGuides.map((item) => (
                    <a
                      key={item.slug}
                      className="lesson-row"
                      href={localizedPath('guide', locale, {
                        guideSlug: item.slug,
                      })}
                    >
                      <span>{ui.guideTag}</span>
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.description}</p>
                      </div>
                      <small>{ui.open}</small>
                    </a>
                  ))}
                  {relatedComparisons.map((item) => (
                    <a
                      key={item.slug}
                      className="lesson-row"
                      href={localizedPath('comparison', locale, {
                        comparisonSlug: item.slug,
                      })}
                    >
                      <span>{ui.comparisonTag}</span>
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.description}</p>
                      </div>
                      <small>{ui.open}</small>
                    </a>
                  ))}
                  {relatedTerms.map((item) => (
                    <a
                      key={item.slug}
                      className="lesson-row"
                      href={localizedPath('glossaryTerm', locale, {
                        termSlug: item.slug,
                      })}
                    >
                      <span>{ui.termTag}</span>
                      <div>
                        <strong>{item.term}</strong>
                        <p>{item.description}</p>
                      </div>
                      <small>{ui.open}</small>
                    </a>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </article>
      </div>
    </main>
  )
}

// ---------------------------------------------------------------------------
// Guides: rich editorial / how-to articles
// ---------------------------------------------------------------------------


export function GuidesIndexPage() {
  const { locale, dict } = useTranslation()
  const catalog = useCatalog()
  const ui = dict.pages.guides
  const items = catalog.guides

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      ...buildItemListJsonLd(
        items.map((item) => ({
          name: item.title,
          path: localizedPath('guide', locale, { guideSlug: item.slug }),
        })),
      ),
    },
    buildBreadcrumbJsonLd([
      { name: dict.pages.breadcrumbs.start, path: localizedPath('home', locale) },
      { name: ui.breadcrumb, path: localizedPath('guides', locale) },
    ]),
  ]

  return (
    <main className="page-shell">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <PageIntro
        eyebrow={ui.eyebrow}
        title={ui.title}
        text={ui.intro}
        actions={
          <>
            <a className="primary-button" href={localizedPath('courses', locale)}>
              {ui.toCourses}
              <ArrowRight size={18} />
            </a>
            <a
              className="ghost-button"
              href={localizedPath('glossary', locale)}
            >
              {ui.toGlossary}
            </a>
          </>
        }
      />
      <section className="fundamentals-group">
        <div className="resource-grid">
          {items.map((item) => (
            <a
              key={item.slug}
              className="resource-card resource-card-guide"
              href={localizedPath('guide', locale, { guideSlug: item.slug })}
            >
              <span className="resource-card-icon" aria-hidden="true">
                <Lightbulb size={20} />
              </span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <small>
                {item.category} · {guideReadingMinutes(item)} {ui.minRead}
              </small>
            </a>
          ))}
        </div>
      </section>
    </main>
  )
}

export function GuidePage({ guideSlug }: { guideSlug: string }) {
  const { locale, dict, formatDate } = useTranslation()
  const catalog = useCatalog()
  const ui = dict.pages.guides
  const guide = catalog.getGuide(guideSlug)
  if (!guide) return <NotFoundPage label={ui.notFound} />

  const relatedGuides = guide.relatedGuideSlugs
    .map((slug) => catalog.getGuide(slug))
    .filter((item): item is Guide => Boolean(item))
  const relatedTerms = guide.relatedTermSlugs
    .map((slug) => catalog.getGlossaryTerm(slug))
    .filter((item): item is GlossaryTerm => Boolean(item))
  const relatedLessons = guide.relatedLessonSlugs
    .map((slug) => catalog.lessons.find((lesson) => lesson.slug === slug))
    .filter((lesson): lesson is Lesson => Boolean(lesson))
  const pillar = guide.pillarSlug ? catalog.getGuide(guide.pillarSlug) : undefined

  const sections = guide.sections.map((section) => ({
    ...section,
    anchorId: slugify(section.heading),
  }))

  const guidePath = localizedPath('guide', locale, { guideSlug: guide.slug })

  const bodyText = [
    guide.intro,
    ...sections.map((section) => `${section.heading}. ${section.body}`),
  ].join('\n\n')

  const jsonLd = [
    {
      ...buildBlogPostingJsonLd({
        title: guide.title,
        description: guide.description,
        body: bodyText,
        datePublished: guide.datePublished,
        path: guidePath,
        keywords: guide.keywords.join(', '),
        locale,
      }),
      dateModified: guide.dateModified,
    },
    buildFaqJsonLd(guide.faq, locale),
    buildBreadcrumbJsonLd([
      { name: dict.pages.breadcrumbs.start, path: localizedPath('home', locale) },
      { name: ui.breadcrumb, path: localizedPath('guides', locale) },
      { name: guide.title, path: guidePath },
    ]),
    ...(guide.howToSteps && guide.howToSteps.length > 0
      ? [
          buildHowToJsonLd({
            name: guide.title,
            description: guide.description,
            steps: guide.howToSteps,
            path: guidePath,
            locale,
          }),
        ]
      : []),
  ]

  return (
    <main className="lesson-page">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <header className="lesson-header" data-hero-sentinel>
        <nav
          className="lesson-breadcrumbs"
          aria-label={dict.common.breadcrumbLabel}
        >
          <a href={localizedPath('guides', locale)}>{ui.breadcrumb}</a>
          <ChevronRight size={12} aria-hidden="true" />
          <span>{guide.category}</span>
        </nav>
        <span className="eyebrow">{ui.eyebrow}</span>
        <h1>{guide.title}</h1>
        <div className="lesson-meta-strip">
          <span>{guide.category}</span>
          <span>•</span>
          <span>
            {guideReadingMinutes(guide)} {ui.minRead}
          </span>
          <span>•</span>
          <span>
            {ui.updated} {formatDate(guide.dateModified)}
          </span>
        </div>
      </header>
      <div className="lesson-layout lesson-detail-layout">
        <article className="lesson-content lesson-detail-content">
          <section className="outcome-panel article-summary-panel">
            <p className="resource-intro">{guide.intro}</p>
            {pillar ? (
              <p className="guide-pillar-link">
                {ui.pillarLabel}:{' '}
                <a
                  href={localizedPath('guide', locale, {
                    guideSlug: pillar.slug,
                  })}
                >
                  {pillar.title}
                </a>
              </p>
            ) : null}
          </section>
          <aside className="lesson-sidebar article-sidebar">
            <details className="lesson-sidebar-details" open>
              <summary>{ui.inThisPage}</summary>
              {sections.map((section) => (
                <a key={section.heading} href={`#${section.anchorId}`}>
                  {section.heading}
                </a>
              ))}
              {guide.howToSteps && guide.howToSteps.length > 0 ? (
                <a href="#steps">{ui.stepsHeading}</a>
              ) : null}
              <a href="#faq">{ui.faqHeading}</a>
            </details>
          </aside>
          <div className="lesson-main-flow">
            {sections.map((section) => (
              <section
                className="lesson-section"
                id={section.anchorId}
                key={section.heading}
              >
                <h2>{section.heading}</h2>
                <LessonSectionBody
                  section={{ title: section.heading, body: section.body, blocks: section.blocks }}
                />
              </section>
            ))}
            {guide.howToSteps && guide.howToSteps.length > 0 ? (
              <section className="lesson-section" id="steps">
                <h2>{ui.stepsHeading}</h2>
                <ol className="howitworks-steps guide-steps">
                  {guide.howToSteps.map((step, index) => (
                    <li key={step.name} className="howitworks-step">
                      <span className="howitworks-number" aria-hidden="true">
                        {(index + 1).toString().padStart(2, '0')}
                      </span>
                      <h3>{step.name}</h3>
                      <p>{step.text}</p>
                    </li>
                  ))}
                </ol>
              </section>
            ) : null}
            <section className="lesson-section" id="faq">
              <h2>{ui.faqHeading}</h2>
              <FaqAccordion items={guide.faq} variant="article" />
            </section>
            {relatedGuides.length ||
            relatedTerms.length ||
            relatedLessons.length ? (
              <section className="lesson-section" id="related">
                <h2>{ui.relatedHeading}</h2>
                <div className="lesson-list">
                  {relatedGuides.map((item) => (
                    <a
                      key={item.slug}
                      className="lesson-row"
                      href={localizedPath('guide', locale, {
                        guideSlug: item.slug,
                      })}
                    >
                      <span>{ui.guideTag}</span>
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.description}</p>
                      </div>
                      <small>{ui.open}</small>
                    </a>
                  ))}
                  {relatedTerms.map((item) => (
                    <a
                      key={item.slug}
                      className="lesson-row"
                      href={localizedPath('glossaryTerm', locale, {
                        termSlug: item.slug,
                      })}
                    >
                      <span>{ui.termTag}</span>
                      <div>
                        <strong>{item.term}</strong>
                        <p>{item.description}</p>
                      </div>
                      <small>{ui.open}</small>
                    </a>
                  ))}
                  {relatedLessons.map((lesson) => (
                    <a
                      key={lesson.slug}
                      className="lesson-row"
                      href={localizedPath('lesson', locale, {
                        courseSlug: lesson.courseSlug,
                        lessonSlug: lesson.slug,
                      })}
                    >
                      <span>{ui.lessonTag}</span>
                      <div>
                        <strong>{lesson.title}</strong>
                        <p>{lesson.outcome}</p>
                      </div>
                      <small>{ui.open}</small>
                    </a>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </article>
      </div>
    </main>
  )
}

// ---------------------------------------------------------------------------
// Tools: indexable shell + client widget (mounted via toolKey)
// ---------------------------------------------------------------------------


export function ToolsIndexPage() {
  const { locale, dict } = useTranslation()
  const catalog = useCatalog()
  const ui = dict.pages.tools
  const items = catalog.tools

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      ...buildItemListJsonLd(
        items.map((item) => ({
          name: item.title,
          path: localizedPath('tool', locale, { toolSlug: item.slug }),
        })),
      ),
    },
    buildBreadcrumbJsonLd([
      { name: dict.pages.breadcrumbs.start, path: localizedPath('home', locale) },
      { name: ui.breadcrumb, path: localizedPath('tools', locale) },
    ]),
  ]

  return (
    <main className="page-shell">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <PageIntro
        eyebrow={ui.eyebrow}
        title={ui.title}
        text={ui.intro}
        actions={
          <>
            <a className="primary-button" href={localizedPath('guides', locale)}>
              {ui.toGuides}
              <ArrowRight size={18} />
            </a>
            <a
              className="ghost-button"
              href={localizedPath('glossary', locale)}
            >
              {ui.toGlossary}
            </a>
          </>
        }
      />
      <section className="fundamentals-group">
        <div className="resource-grid">
          {items.map((item) => (
            <a
              key={item.slug}
              className="resource-card resource-card-guide"
              href={localizedPath('tool', locale, { toolSlug: item.slug })}
            >
              <span className="resource-card-icon" aria-hidden="true">
                <Wrench size={20} />
              </span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <small>
                {item.category} · {toolReadingMinutes(item)} {ui.minRead}
              </small>
            </a>
          ))}
        </div>
      </section>
    </main>
  )
}

export function ToolPage({
  toolSlug,
  renderWidget,
}: {
  toolSlug: string
  // The interactive widget is injected by the route (see tool-widget.tsx) so
  // its heavy client code + logic libs stay in the tool route's own chunk
  // instead of the shared `pages.tsx` barrel that every route loads.
  renderWidget: (tool: Tool) => ReactNode
}) {
  const { locale, dict, formatDate } = useTranslation()
  const catalog = useCatalog()
  const ui = dict.pages.tools
  const tool = catalog.getTool(toolSlug)
  if (!tool) return <NotFoundPage label={ui.notFound} />

  const relatedGuides = tool.relatedGuideSlugs
    .map((slug) => catalog.getGuide(slug))
    .filter((item): item is Guide => Boolean(item))
  const relatedTerms = tool.relatedTermSlugs
    .map((slug) => catalog.getGlossaryTerm(slug))
    .filter((item): item is GlossaryTerm => Boolean(item))

  const sections = tool.sections.map((section) => ({
    ...section,
    anchorId: slugify(section.title),
  }))

  const toolPath = localizedPath('tool', locale, { toolSlug: tool.slug })

  const jsonLd = [
    buildSoftwareAppJsonLd({
      name: tool.title,
      description: tool.description,
      path: toolPath,
      locale,
    }),
    buildFaqJsonLd(tool.faq, locale),
    buildBreadcrumbJsonLd([
      { name: dict.pages.breadcrumbs.start, path: localizedPath('home', locale) },
      { name: ui.breadcrumb, path: localizedPath('tools', locale) },
      { name: tool.title, path: toolPath },
    ]),
  ]

  return (
    <main className="lesson-page">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <header className="lesson-header" data-hero-sentinel>
        <nav
          className="lesson-breadcrumbs"
          aria-label={dict.common.breadcrumbLabel}
        >
          <a href={localizedPath('tools', locale)}>{ui.breadcrumb}</a>
          <ChevronRight size={12} aria-hidden="true" />
          <span>{tool.title}</span>
        </nav>
        <span className="eyebrow">{ui.eyebrow}</span>
        <h1>{tool.title}</h1>
        <div className="lesson-meta-strip">
          <span>{tool.category}</span>
          <span>•</span>
          <span>
            {toolReadingMinutes(tool)} {ui.minRead}
          </span>
          <span>•</span>
          <span>
            {ui.updated} {formatDate(tool.updatedAt)}
          </span>
        </div>
      </header>
      <div className="lesson-layout lesson-detail-layout">
        <article className="lesson-content lesson-detail-content">
          <section className="outcome-panel article-summary-panel">
            <p className="resource-intro">{tool.intro}</p>
          </section>
          <aside className="lesson-sidebar article-sidebar">
            <details className="lesson-sidebar-details" open>
              <summary>{ui.inThisPage}</summary>
              <a href="#widget">{ui.widgetHeading}</a>
              {sections.map((section) => (
                <a key={section.title} href={`#${section.anchorId}`}>
                  {section.title}
                </a>
              ))}
              <a href="#faq">{ui.faqHeading}</a>
            </details>
          </aside>
          <div className="lesson-main-flow">
            <section className="lesson-section" id="widget">
              <h2>{ui.widgetHeading}</h2>
              {renderWidget(tool)}
            </section>
            <ToolLeadCapture toolSlug={tool.slug} toolTitle={tool.title} />
            <h2 className="tool-about-heading">{ui.aboutHeading}</h2>
            {sections.map((section) => (
              <section
                className="lesson-section"
                id={section.anchorId}
                key={section.title}
              >
                <h3>{section.title}</h3>
                <LessonSectionBody section={section} />
              </section>
            ))}
            <section className="lesson-section" id="faq">
              <h2>{ui.faqHeading}</h2>
              <FaqAccordion items={tool.faq} variant="article" />
            </section>
            {relatedGuides.length || relatedTerms.length ? (
              <section className="lesson-section" id="related">
                <h2>{ui.relatedHeading}</h2>
                <div className="lesson-list">
                  {relatedGuides.map((item) => (
                    <a
                      key={item.slug}
                      className="lesson-row"
                      href={localizedPath('guide', locale, {
                        guideSlug: item.slug,
                      })}
                    >
                      <span>{ui.guideTag}</span>
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.description}</p>
                      </div>
                      <small>{ui.open}</small>
                    </a>
                  ))}
                  {relatedTerms.map((item) => (
                    <a
                      key={item.slug}
                      className="lesson-row"
                      href={localizedPath('glossaryTerm', locale, {
                        termSlug: item.slug,
                      })}
                    >
                      <span>{ui.termTag}</span>
                      <div>
                        <strong>{item.term}</strong>
                        <p>{item.description}</p>
                      </div>
                      <small>{ui.open}</small>
                    </a>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </article>
      </div>
    </main>
  )
}

// ---------------------------------------------------------------------------
// Builds: founder project case studies
// ---------------------------------------------------------------------------

// Order build categories deterministically on the index and about grids.
const BUILD_CATEGORY_ORDER: BuildCategory[] = [
  'saas',
  'automation',
  'tool',
  'content',
]

const BUILD_CATEGORY_ICON: Record<BuildCategory, typeof Boxes> = {
  saas: Boxes,
  automation: Workflow,
  tool: Wrench,
  content: FileText,
}


export function BuildsIndexPage() {
  const { locale, dict } = useTranslation()
  const catalog = useCatalog()
  const ui = dict.pages.builds
  const items = catalog.builds

  const grouped = BUILD_CATEGORY_ORDER.map((category) => ({
    category,
    items: items.filter((item) => item.category === category),
  })).filter((group) => group.items.length > 0)

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      ...buildItemListJsonLd(
        items.map((item) => ({
          name: item.title,
          path: localizedPath('build', locale, { buildSlug: item.slug }),
        })),
      ),
    },
    buildBreadcrumbJsonLd([
      { name: dict.pages.breadcrumbs.start, path: localizedPath('home', locale) },
      { name: ui.breadcrumb, path: localizedPath('builds', locale) },
    ]),
  ]

  return (
    <main className="page-shell">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <PageIntro
        eyebrow={ui.eyebrow}
        title={ui.title}
        text={ui.intro}
        actions={
          <>
            <a className="primary-button" href={localizedPath('about', locale)}>
              {ui.toAbout}
              <ArrowRight size={18} />
            </a>
            <a className="ghost-button" href={localizedPath('courses', locale)}>
              {ui.toCourses}
            </a>
          </>
        }
      />
      {grouped.map((group) => {
        const Icon = BUILD_CATEGORY_ICON[group.category]
        return (
          <section className="fundamentals-group" key={group.category}>
            <h2 className="fundamentals-group-title">
              {ui.categories[group.category]}
            </h2>
            <div className="resource-grid">
              {group.items.map((item) => (
                <a
                  key={item.slug}
                  className="resource-card resource-card-guide build-card"
                  href={localizedPath('build', locale, { buildSlug: item.slug })}
                >
                  <span className="resource-card-icon" aria-hidden="true">
                    <Icon size={20} />
                  </span>
                  <span className="build-card-status" data-status={item.status}>
                    {ui.statusLabel[item.status]}
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.tagline}</p>
                  <div className="build-card-stack">
                    {item.stack.slice(0, 4).map((tech) => (
                      <span className="build-stack-chip" key={tech}>
                        {tech}
                      </span>
                    ))}
                  </div>
                  <small>
                    {ui.categories[item.category]} · {buildReadingMinutes(item)}{' '}
                    {ui.minRead}
                  </small>
                </a>
              ))}
            </div>
          </section>
        )
      })}
    </main>
  )
}

export function BuildPage({ buildSlug }: { buildSlug: string }) {
  const { locale, dict, formatDate } = useTranslation()
  const catalog = useCatalog()
  const ui = dict.pages.builds
  const build = catalog.getBuild(buildSlug)
  if (!build) return <NotFoundPage label={ui.notFound} />

  const relatedLessons = build.relatedLessonSlugs
    .map((slug) => catalog.lessons.find((lesson) => lesson.slug === slug))
    .filter((lesson): lesson is Lesson => Boolean(lesson))
  const relatedFundamentals = build.relatedFundamentalSlugs
    .map((slug) => catalog.getFundamental(slug))
    .filter((item): item is Fundamental => Boolean(item))

  const sections = build.sections.map((section) => ({
    ...section,
    anchorId: slugify(section.title),
  }))

  const buildPath = localizedPath('build', locale, { buildSlug: build.slug })

  const bodyText = [
    build.tagline,
    ...sections.map((section) => `${section.title}. ${section.body}`),
    ...build.lessonsLearned,
  ].join('\n\n')

  const jsonLd = [
    {
      ...buildBlogPostingJsonLd({
        title: build.title,
        description: build.description,
        body: bodyText,
        datePublished: build.updatedAt,
        path: buildPath,
        keywords: build.keywords.join(', '),
        locale,
      }),
      dateModified: build.updatedAt,
    },
    buildBreadcrumbJsonLd([
      { name: dict.pages.breadcrumbs.start, path: localizedPath('home', locale) },
      { name: ui.breadcrumb, path: localizedPath('builds', locale) },
      { name: build.title, path: buildPath },
    ]),
  ]

  return (
    <main className="lesson-page">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <header className="lesson-header" data-hero-sentinel>
        <nav
          className="lesson-breadcrumbs"
          aria-label={dict.common.breadcrumbLabel}
        >
          <a href={localizedPath('builds', locale)}>{ui.breadcrumb}</a>
          <ChevronRight size={12} aria-hidden="true" />
          <span>{ui.categories[build.category]}</span>
        </nav>
        <span className="eyebrow">{ui.eyebrow}</span>
        <h1>{build.title}</h1>
        <p className="lesson-lead">{build.tagline}</p>
        <div className="lesson-meta-strip">
          <span className="build-card-status" data-status={build.status}>
            {ui.statusLabel[build.status]}
          </span>
          <span>•</span>
          <span>{ui.categories[build.category]}</span>
          <span>•</span>
          <span>
            {buildReadingMinutes(build)} {ui.minRead}
          </span>
          <span>•</span>
          <span>
            {ui.updated} {formatDate(build.updatedAt)}
          </span>
        </div>
      </header>
      <div className="lesson-layout lesson-detail-layout">
        <article className="lesson-content lesson-detail-content">
          <section className="outcome-panel article-summary-panel">
            <h2>{ui.stackLabel}</h2>
            <div className="build-card-stack">
              {build.stack.map((tech) => (
                <span className="build-stack-chip" key={tech}>
                  {tech}
                </span>
              ))}
            </div>
          </section>
          <aside className="lesson-sidebar article-sidebar">
            <details className="lesson-sidebar-details" open>
              <summary>{ui.inThisPage}</summary>
              {sections.map((section) => (
                <a key={section.title} href={`#${section.anchorId}`}>
                  {section.title}
                </a>
              ))}
              <a href="#lessons-learned">{ui.lessonsHeading}</a>
            </details>
          </aside>
          <div className="lesson-main-flow">
            {sections.map((section) => (
              <section
                className="lesson-section"
                id={section.anchorId}
                key={section.title}
              >
                <h2>{section.title}</h2>
                <LessonSectionBody section={section} />
              </section>
            ))}
            <section
              className="lesson-section build-lessons-panel"
              id="lessons-learned"
            >
              <h2>
                <Lightbulb size={20} aria-hidden="true" /> {ui.lessonsHeading}
              </h2>
              <ul className="build-lessons-list">
                {build.lessonsLearned.map((lesson) => (
                  <li key={lesson}>{lesson}</li>
                ))}
              </ul>
            </section>
            {relatedLessons.length || relatedFundamentals.length ? (
              <section className="lesson-section" id="related">
                <h2>{ui.relatedHeading}</h2>
                <div className="lesson-list">
                  {relatedLessons.map((lesson) => (
                    <a
                      key={lesson.slug}
                      className="lesson-row"
                      href={localizedPath('lesson', locale, {
                        courseSlug: lesson.courseSlug,
                        lessonSlug: lesson.slug,
                      })}
                    >
                      <span>{ui.lessonTag}</span>
                      <div>
                        <strong>{lesson.title}</strong>
                        <p>{lesson.outcome}</p>
                      </div>
                      <small>{ui.open}</small>
                    </a>
                  ))}
                  {relatedFundamentals.map((item) => (
                    <a
                      key={item.slug}
                      className="lesson-row"
                      href={localizedPath('fundamental', locale, {
                        fundamentalSlug: item.slug,
                      })}
                    >
                      <span>{ui.fundamentalTag}</span>
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.description}</p>
                      </div>
                      <small>{ui.open}</small>
                    </a>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </article>
      </div>
    </main>
  )
}

// ---------------------------------------------------------------------------
// About / Founder page
// ---------------------------------------------------------------------------

export function AboutPage() {
  const { locale, dict } = useTranslation()
  const catalog = useCatalog()
  const ui = dict.pages.about
  const builds = catalog.builds

  const grouped = BUILD_CATEGORY_ORDER.map((category) => ({
    category,
    items: builds.filter((item) => item.category === category),
  })).filter((group) => group.items.length > 0)

  const jsonLd = [
    buildPersonJsonLd(),
    buildBreadcrumbJsonLd([
      { name: dict.pages.breadcrumbs.start, path: localizedPath('home', locale) },
      { name: ui.eyebrow, path: localizedPath('about', locale) },
    ]),
  ]

  return (
    <main className="page-shell about-page">
      {jsonLd.map((entry) => (
        <script
          key={entry['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(entry) }}
        />
      ))}
      <section className="about-hero">
        <figure className="founder-portrait about-portrait">
          <picture>
            <source
              type="image/webp"
              srcSet="/founder-nico-420.webp 420w, /founder-nico-640.webp 640w, /founder-nico.webp 800w"
              sizes="(max-width: 980px) 320px, 360px"
            />
            <img
              src="/founder-nico.jpg"
              srcSet="/founder-nico-420.jpg 420w, /founder-nico-640.jpg 640w, /founder-nico.jpg 800w"
              sizes="(max-width: 980px) 320px, 360px"
              alt={ui.imageAlt}
              width={800}
              height={1200}
              loading="eager"
              decoding="async"
            />
          </picture>
        </figure>
        <div className="about-hero-copy">
          <span className="eyebrow">{ui.eyebrow}</span>
          <h1>{ui.title}</h1>
          <p className="about-intro">{ui.intro}</p>
          <div className="hero-actions page-intro-actions">
            <a className="primary-button" href={localizedPath('builds', locale)}>
              {ui.toBuilds}
              <ArrowRight size={18} />
            </a>
            <a className="ghost-button" href={localizedPath('courses', locale)}>
              {ui.toCourses}
            </a>
          </div>
        </div>
      </section>

      <section className="about-prose">
        <h2>{ui.bioHeading}</h2>
        {ui.bio.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </section>

      <section className="about-prose">
        <h2>{ui.journeyHeading}</h2>
        {ui.journey.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </section>

      <section className="about-philosophy">
        <h2 className="fundamentals-group-title">{ui.philosophyHeading}</h2>
        <div className="problem-grid">
          {ui.philosophy.map((item) => (
            <article className="problem-card" key={item.title}>
              <span className="problem-card-icon" aria-hidden="true">
                <Sparkles size={22} />
              </span>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-builds">
        <div className="section-heading">
          <span className="eyebrow">
            <Hammer size={14} aria-hidden="true" /> {dict.pages.builds.breadcrumb}
          </span>
          <h2>{ui.projectsHeading}</h2>
          <p>{ui.projectsLead}</p>
        </div>
        {grouped.map((group) => {
          const Icon = BUILD_CATEGORY_ICON[group.category]
          return (
            <div className="fundamentals-group" key={group.category}>
              <h3 className="fundamentals-group-title">
                {ui.categories[group.category]}
              </h3>
              <div className="resource-grid">
                {group.items.map((item) => (
                  <a
                    key={item.slug}
                    className="resource-card resource-card-guide build-card"
                    href={localizedPath('build', locale, {
                      buildSlug: item.slug,
                    })}
                  >
                    <span className="resource-card-icon" aria-hidden="true">
                      <Icon size={20} />
                    </span>
                    <span
                      className="build-card-status"
                      data-status={item.status}
                    >
                      {ui.statusLabel[item.status]}
                    </span>
                    <h4>{item.title}</h4>
                    <p>{item.tagline}</p>
                    <small className="build-card-open">
                      {dict.pages.builds.open}
                      <ExternalLink size={13} aria-hidden="true" />
                    </small>
                  </a>
                ))}
              </div>
            </div>
          )
        })}
      </section>
    </main>
  )
}

/** Bucket a changelog entry into a recency group relative to `now` (UTC days). */
function changelogGroupKey(
  publishedAt: string,
  now: number,
): 'today' | 'yesterday' | 'thisWeek' | 'earlier' {
  const published = Date.parse(`${publishedAt}T00:00:00Z`)
  const today = Date.parse(`${utcDateKey(now)}T00:00:00Z`)
  const diffDays = Math.round((today - published) / 86_400_000)
  if (diffDays <= 0) return 'today'
  if (diffDays === 1) return 'yesterday'
  if (diffDays <= 7) return 'thisWeek'
  return 'earlier'
}

export function ChangelogPage() {
  const { locale, t } = useTranslation()
  const catalog = useCatalog()
  const changelogEntries = catalog.changelogEntries
  const [tool, setTool] = useState<string>('all')
  // The chips are server-rendered; a click before hydration would be silently
  // lost. Keep them disabled until React owns them, so no tap is ever dead
  // (and automation waits for them to become actionable).
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => setHydrated(true), [])

  // The tool list is derived from the data, not hard-coded, so adding a
  // changelog entry for a new tool surfaces a new filter automatically.
  const tools = useMemo(
    () =>
      Array.from(new Set(changelogEntries.map((entry) => entry.tool))).sort(),
    [changelogEntries],
  )

  const filtered = useMemo(
    () =>
      changelogEntries.filter((entry) => tool === 'all' || entry.tool === tool),
    [changelogEntries, tool],
  )

  // Group by recency. Entries are kept in their original (newest-first) order
  // inside each bucket.
  const groups = useMemo(() => {
    const now = Date.now()
    const buckets: Record<
      'today' | 'yesterday' | 'thisWeek' | 'earlier',
      typeof changelogEntries
    > = { today: [], yesterday: [], thisWeek: [], earlier: [] }
    const sorted = [...filtered].sort(
      (a, b) => Date.parse(b.publishedAt) - Date.parse(a.publishedAt),
    )
    for (const entry of sorted) {
      buckets[changelogGroupKey(entry.publishedAt, now)].push(entry)
    }
    return buckets
  }, [filtered])

  const groupOrder: Array<{
    key: 'today' | 'yesterday' | 'thisWeek' | 'earlier'
    label: string
  }> = [
    { key: 'today', label: t('pages.changelog.groupToday') },
    { key: 'yesterday', label: t('pages.changelog.groupYesterday') },
    { key: 'thisWeek', label: t('pages.changelog.groupThisWeek') },
    { key: 'earlier', label: t('pages.changelog.groupEarlier') },
  ]

  return (
    <main className="page-shell">
      <PageIntro
        eyebrow={t('pages.changelog.eyebrow')}
        title={t('pages.changelog.title')}
        text={t('pages.changelog.text')}
        actions={
          <>
            <a
              className="primary-button"
              href={localizedPath('newsletter', locale)}
            >
              {t('pages.changelog.subscribe')}
              <ArrowRight size={18} />
            </a>
            <a
              className="ghost-button"
              href={localizedPath('resources', locale)}
            >
              {t('pages.common.viewResources')}
            </a>
          </>
        }
      />

      <div
        className="changelog-filter"
        role="group"
        aria-label={t('pages.changelog.filterLabel')}
      >
        <Filter size={16} aria-hidden="true" />
        <button
          type="button"
          className={`chip${tool === 'all' ? ' is-active' : ''}`}
          aria-pressed={tool === 'all'}
          onClick={() => setTool('all')}
          disabled={!hydrated}
        >
          {t('pages.changelog.filterAll')}
        </button>
        {tools.map((toolName) => (
          <button
            key={toolName}
            type="button"
            className={`chip${tool === toolName ? ' is-active' : ''}`}
            aria-pressed={tool === toolName}
            onClick={() => setTool(toolName)}
            disabled={!hydrated}
          >
            {toolName}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="form-status">{t('pages.changelog.empty')}</p>
      ) : (
        groupOrder.map((group) =>
          groups[group.key].length > 0 ? (
            <section key={group.key} className="changelog-group">
              <h2 className="changelog-group-title">{group.label}</h2>
              <div className="post-list">
                {groups[group.key].map((entry) => (
                  <a
                    key={entry.slug}
                    className="post-row"
                    href={localizedPath('changelogEntry', locale, {
                      entrySlug: entry.slug,
                    })}
                  >
                    <span>{entry.tool}</span>
                    <div>
                      <strong>{entry.title}</strong>
                      <p>{entry.excerpt}</p>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          ) : null,
        )
      )}
    </main>
  )
}

export function ChangelogEntryPage({ entrySlug }: { entrySlug: string }) {
  const { locale, t, dict } = useTranslation()
  const entry = useCatalog().getChangelogEntry(entrySlug)
  if (!entry) return <NotFoundPage label={t('pages.notFound.changelogEntry')} />
  const jsonLd = [
    buildBlogPostingJsonLd({
      title: entry.title,
      description: entry.excerpt,
      body: entry.impact,
      datePublished: entry.publishedAt,
      path: localizedPath('changelogEntry', locale, { entrySlug: entry.slug }),
      keywords: entry.tags.join(', '),
      locale,
    }),
    buildBreadcrumbJsonLd([
      { name: t('pages.breadcrumbs.start'), path: localizedPath('home', locale) },
      {
        name: t('pages.breadcrumbs.changelog'),
        path: localizedPath('changelog', locale),
      },
      {
        name: entry.title,
        path: localizedPath('changelogEntry', locale, {
          entrySlug: entry.slug,
        }),
      },
    ]),
  ]

  return (
    <main className="page-shell narrow">
      {jsonLd.map((node) => (
        <script
          key={node['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(node) }}
        />
      ))}
      <PageIntro
        eyebrow={entry.tool}
        title={entry.title}
        text={entry.excerpt}
      />
      <article className="editorial-block">
        {dict.pages.changelog.entrySections.map((title) => (
          <section key={title}>
            <h2>{title}</h2>
            <p>{entry.impact}</p>
          </section>
        ))}
      </article>
    </main>
  )
}

export function SearchPage() {
  const { locale, t } = useTranslation()
  const [query, setQuery] = useState('')
  const results = useMemo(
    () => buildSearchResults(query, locale),
    [query, locale],
  )

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    setQuery(params.get('q') ?? '')
  }, [])

  return (
    <main className="page-shell narrow">
      <PageIntro
        eyebrow={t('pages.search.eyebrow')}
        title={t('pages.search.title')}
        text={t('pages.search.text')}
      />
      <div className="search-box">
        <input
          aria-label={t('pages.search.inputLabel')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('search.emptyPrompt')}
        />
      </div>
      <div className="search-results page-search-results">
        {results.length ? (
          results.map((group) => (
            <section key={group.group}>
              <h2>{t(`search.groups.${group.group}`)}</h2>
              {group.items.map((item) => (
                <a key={item.href} href={item.href}>
                  <strong>
                    {item.title}
                    {item.pro ? <ProBadge className="is-inline" /> : null}
                  </strong>
                  <span>{item.description}</span>
                </a>
              ))}
            </section>
          ))
        ) : (
          <SearchEmptyState />
        )}
      </div>
    </main>
  )
}

export function NewsletterPage() {
  const { t } = useTranslation()
  return (
    <main className="page-shell narrow">
      <PageIntro
        eyebrow={t('pages.newsletter.eyebrow')}
        title={t('pages.newsletter.title')}
        text={t('pages.newsletter.text')}
      />
    </main>
  )
}

// Canonical, locale-stable topic values submitted to the backend. The visible
// labels are localized (`pages.contact.topics`) and rendered by index, so a
// German label and an English label still post the same stored value.
const TOPIC_VALUES = [
  'Frage zum Campus',
  'Kooperation',
  'Workflow Review',
  'Beratung',
  'Technisches Problem',
  'Datenschutz',
  'Sonstiges',
] as const

export function ContactPage() {
  const { locale, t, dict } = useTranslation()
  const submitContact = useMutation(api.forms.submitContact)
  const [status, setStatus] = useState<'idle' | 'saving' | 'success' | 'error'>(
    'idle',
  )

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setStatus('saving')
    try {
      await submitContact({
        name: String(form.get('name') ?? ''),
        email: String(form.get('email') ?? ''),
        company: String(form.get('company') ?? '') || undefined,
        website: String(form.get('website') ?? '') || undefined,
        topic: String(form.get('topic') ?? 'Frage zum Campus'),
        message: String(form.get('message') ?? ''),
        anonId: getCookie(anonIdCookieName) ?? undefined,
      })
      setStatus('success')
      event.currentTarget.reset()
    } catch {
      setStatus('error')
    }
  }

  return (
    <main className="page-shell narrow">
      <PageIntro
        eyebrow={t('pages.contact.eyebrow')}
        title={t('pages.contact.title')}
        text={t('pages.contact.text')}
      />
      <form className="form-grid" onSubmit={onSubmit}>
        <input
          name="name"
          placeholder={t('pages.contact.nameLabel')}
          aria-label={t('pages.contact.nameLabel')}
          required
        />
        <input
          type="email"
          name="email"
          placeholder={t('pages.contact.emailLabel')}
          aria-label={t('pages.contact.emailLabel')}
          required
        />
        <input
          name="company"
          placeholder={t('pages.contact.companyPlaceholder')}
          aria-label={t('pages.contact.companyLabel')}
        />
        <input
          className="hp-field"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />
        <select
          name="topic"
          aria-label={t('pages.contact.topicLabel')}
          defaultValue="Frage zum Campus"
        >
          {TOPIC_VALUES.map((value, index) => (
            <option key={value} value={value}>
              {dict.pages.contact.topics[index]}
            </option>
          ))}
        </select>
        <textarea
          name="message"
          placeholder={t('pages.contact.messageLabel')}
          aria-label={t('pages.contact.messageLabel')}
          required
        />
        <label>
          <input type="checkbox" required /> {t('pages.contact.consentPrefix')}{' '}
          <a href={localizedPath('privacy', locale)}>
            {t('pages.contact.consentLink')}
          </a>
          .
        </label>
        <button className="primary-button" type="submit">
          {status === 'saving'
            ? t('pages.contact.sending')
            : t('pages.contact.submit')}
        </button>
        <FormStatus status={status} success={t('pages.contact.success')} />
      </form>
    </main>
  )
}

export function SettingsPage({ section = 'übersicht' }: { section?: string }) {
  const { t } = useTranslation()
  return (
    <main className="page-shell">
      <PageIntro
        eyebrow={t('pages.settings.eyebrow')}
        title={t('pages.settings.title')}
        text={t('pages.settings.text')}
      />
      <div className="settings-layout">
        <SettingsNav
          current={
            section === 'datenschutz'
              ? 'settingsPrivacy'
              : section === 'benachrichtigungen'
                ? 'settingsNotifications'
                : undefined
          }
        />
        <section className="editorial-block">
          <SettingsCopy section={section} />
        </section>
      </div>
    </main>
  )
}

function SettingsCopy({ section }: { section: string }) {
  const { locale, t } = useTranslation()
  const { isLoaded, isSignedIn } = useAppAuth()
  const exportData = useQuery(api.users.exportMyData, isSignedIn ? {} : 'skip')
  const updateProfile = useMutation(api.users.updateProfile)
  const updatePreferences = useMutation(api.users.updatePreferences)
  const requestDeletion = useMutation(api.users.requestAccountDeletion)
  const [profileStatus, setProfileStatus] = useState<FormStatusState>('idle')
  const [preferenceStatus, setPreferenceStatus] =
    useState<FormStatusState>('idle')
  const [deleteStatus, setDeleteStatus] = useState<FormStatusState>('idle')
  const title =
    section === 'datenschutz'
      ? t('pages.settings.titlePrivacy')
      : section === 'benachrichtigungen'
        ? t('pages.settings.titleNotifications')
        : t('pages.settings.titleOverview')

  if (isLoaded && !isSignedIn) {
    return (
      <section>
        <h2>{title}</h2>
        <p>{t('pages.settings.signedOut')}</p>
        <a className="primary-button" href={localizedPath('login', locale)}>
          {t('pages.settings.signIn')}
        </a>
      </section>
    )
  }

  async function onProfileSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setProfileStatus('saving')
    try {
      await updateProfile({
        name: String(form.get('name') ?? ''),
        username: String(form.get('username') ?? '') || undefined,
        bio: String(form.get('bio') ?? '') || undefined,
        company: String(form.get('company') ?? '') || undefined,
        website: String(form.get('website') ?? '') || undefined,
        publicProfile: form.get('publicProfile') === 'on',
      })
      setProfileStatus('success')
    } catch {
      setProfileStatus('error')
    }
  }

  async function onPreferencesSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setPreferenceStatus('saving')
    try {
      await updatePreferences({
        showPublicProfile: form.get('showPublicProfile') === 'on',
        allowProductEmails: form.get('allowProductEmails') === 'on',
        allowCommunityEmails: form.get('allowCommunityEmails') === 'on',
        allowProgressReminders: form.get('allowProgressReminders') === 'on',
        allowChangelogDigest: form.get('allowChangelogDigest') === 'on',
      })
      setPreferenceStatus('success')
    } catch {
      setPreferenceStatus('error')
    }
  }

  async function onDeleteSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setDeleteStatus('saving')
    try {
      await requestDeletion({
        confirmation: String(form.get('confirmation') ?? ''),
      })
      setDeleteStatus('success')
    } catch {
      setDeleteStatus('error')
    }
  }

  function downloadExport() {
    if (!exportData) return
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'agentic-engineering-campus-export.json'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <section>
      <h2>{title}</h2>
      {section === 'datenschutz' ? (
        <div className="settings-panel-grid">
          <div>
            <h3>{t('pages.settings.dataExportTitle')}</h3>
            <p>{t('pages.settings.dataExportText')}</p>
            <button
              className="ghost-button"
              type="button"
              onClick={downloadExport}
              disabled={!exportData}
            >
              {t('pages.settings.dataExportButton')}
            </button>
            <pre
              className="data-preview"
              aria-label={t('pages.settings.dataExportSummary')}
            >
              {exportData
                ? JSON.stringify(
                    {
                      saves: exportData.saves.length,
                      likes: exportData.reactions.length,
                      progress: exportData.progress.length,
                      comments: exportData.comments.length,
                    },
                    null,
                    2,
                  )
                : t('pages.settings.dataExportLoading')}
            </pre>
          </div>
          <div>
            <h3>{t('pages.settings.deleteTitle')}</h3>
            <p>{t('pages.settings.deleteText')}</p>
            <p>
              {t('pages.settings.deleteHint', {
                phrase: t('pages.settings.deletePhrase'),
              })}
            </p>
            <form className="form-grid compact-form" onSubmit={onDeleteSubmit}>
              <input
                name="confirmation"
                aria-label={t('pages.settings.deleteConfirmLabel')}
                placeholder={t('pages.settings.deletePhrase')}
                required
              />
              <button className="ghost-button danger" type="submit">
                {deleteStatus === 'saving'
                  ? t('pages.settings.deleting')
                  : t('pages.settings.deleteButton')}
              </button>
              <FormStatus
                status={deleteStatus}
                success={t('pages.settings.deleteSuccess')}
              />
            </form>
          </div>
        </div>
      ) : section === 'benachrichtigungen' ? (
        <>
        <SettingsNewsletter />
        <form
          className="settings-panel-grid"
          onSubmit={onPreferencesSubmit}
          key={exportData?.preferences?._id ?? 'preferences'}
        >
          <label className="setting-toggle">
            <input
              name="allowProductEmails"
              type="checkbox"
              defaultChecked={
                exportData?.preferences?.allowProductEmails ?? true
              }
            />
            <span>{t('pages.settings.notifProduct')}</span>
          </label>
          <label className="setting-toggle">
            <input
              name="allowCommunityEmails"
              type="checkbox"
              defaultChecked={
                exportData?.preferences?.allowCommunityEmails ?? false
              }
            />
            <span>{t('pages.settings.notifCommunity')}</span>
          </label>
          <label className="setting-toggle">
            <input
              name="allowProgressReminders"
              type="checkbox"
              defaultChecked={
                exportData?.preferences?.allowProgressReminders ?? true
              }
            />
            <span>{t('pages.settings.notifProgress')}</span>
          </label>
          <label className="setting-toggle">
            <input
              name="allowChangelogDigest"
              type="checkbox"
              defaultChecked={
                exportData?.preferences?.allowChangelogDigest ?? false
              }
            />
            <span>{t('pages.settings.notifChangelogDigest')}</span>
          </label>
          <label className="setting-toggle">
            <input
              name="showPublicProfile"
              type="checkbox"
              defaultChecked={
                exportData?.preferences?.showPublicProfile ?? false
              }
            />
            <span>{t('pages.settings.notifPublicProfile')}</span>
          </label>
          <button className="primary-button" type="submit">
            {preferenceStatus === 'saving'
              ? t('pages.settings.notifSaving')
              : t('pages.settings.notifSave')}
          </button>
          <FormStatus
            status={preferenceStatus}
            success={t('pages.settings.notifSuccess')}
          />
        </form>
        </>
      ) : (
        <form
          className="settings-panel-grid"
          onSubmit={onProfileSubmit}
          key={exportData?.user._id ?? 'profile'}
        >
          <input
            name="name"
            aria-label={t('pages.settings.profileNameLabel')}
            placeholder={t('pages.settings.profileNameLabel')}
            defaultValue={exportData?.user.name ?? ''}
            required
          />
          <input
            name="username"
            aria-label={t('pages.settings.profileUsernameLabel')}
            placeholder={t('pages.settings.profileUsernameLabel')}
            defaultValue={exportData?.user.username ?? ''}
          />
          <input
            name="company"
            aria-label={t('pages.settings.profileCompanyLabel')}
            placeholder={t('pages.settings.profileCompanyLabel')}
            defaultValue={exportData?.profile?.company ?? ''}
          />
          <input
            name="website"
            aria-label={t('pages.settings.profileWebsiteLabel')}
            placeholder="https://..."
            defaultValue={exportData?.profile?.website ?? ''}
          />
          <textarea
            name="bio"
            aria-label={t('pages.settings.profileBioLabel')}
            placeholder={t('pages.settings.profileBioPlaceholder')}
            defaultValue={exportData?.profile?.bio ?? ''}
          />
          <label className="setting-toggle">
            <input
              name="publicProfile"
              type="checkbox"
              defaultChecked={exportData?.profile?.publicProfile ?? false}
            />
            <span>{t('pages.settings.profilePublicToggle')}</span>
          </label>
          <div className="button-row">
            <button className="primary-button" type="submit">
              {profileStatus === 'saving'
                ? t('pages.settings.profileSaving')
                : t('pages.settings.profileSave')}
            </button>
            <a
              className="ghost-button"
              href={localizedPath('settingsProfile', locale)}
            >
              {t('pages.settings.profileManageAvatar')}
            </a>
          </div>
          <FormStatus
            status={profileStatus}
            success={t('pages.settings.profileSuccess')}
          />
        </form>
      )}
    </section>
  )
}

export function SavedPage() {
  const { t } = useTranslation()
  return (
    <UtilityPage
      eyebrow={t('pages.utility.savedEyebrow')}
      title={t('pages.utility.savedTitle')}
      text={t('pages.utility.savedText')}
    />
  )
}

/**
 * Read the anonymous local state from localStorage (client-only). Mirrors the
 * helper in app-shell, kept local so the dashboard works without coupling.
 */
function readLocalAnonymousState() {
  if (typeof window === 'undefined') return parseAnonymousState({})
  try {
    return parseAnonymousState(
      JSON.parse(window.localStorage.getItem(pendingStateStorageName) ?? '{}'),
    )
  } catch {
    return parseAnonymousState({})
  }
}

type DashboardStats = {
  currentStreak: number
  longestStreak: number
  totalMinutes: number
  lessonsCompleted: number
  activeToday: boolean
  courses: Array<{
    courseSlug: string
    completed: number
    total: number
    percent: number
    eligibleForCertificate: boolean
  }>
}

/** Compute the anonymous-equivalent dashboard stats from local state. */
function computeAnonymousDashboard(catalog: Catalog): DashboardStats {
  const state = readLocalAnonymousState()
  const streak = computeStreakStats(activityToDailyList(state), Date.now())
  const completedSlugs = Object.entries(state.progress)
    .filter(([, value]) => value.completed)
    .map(([slug]) => slug)
  const courseStats = catalog.courses.map((course) => {
    const lessonSlugs = catalog
      .getLessonsForCourse(course.slug)
      .map((lesson) => lesson.slug)
    const eligibility = certificateEligibility({
      courseLessonSlugs: lessonSlugs,
      completedLessonSlugs: completedSlugs,
    })
    return {
      courseSlug: course.slug,
      completed: eligibility.completed,
      total: eligibility.total,
      percent:
        eligibility.total > 0
          ? Math.round((eligibility.completed / eligibility.total) * 100)
          : 0,
      eligibleForCertificate: eligibility.eligible,
    }
  })
  return {
    currentStreak: streak.currentStreak,
    longestStreak: streak.longestStreak,
    totalMinutes: streak.totalMinutes,
    lessonsCompleted: completedSlugs.length,
    activeToday: streak.activeToday,
    courses: courseStats,
  }
}

function StatTile({
  icon,
  value,
  label,
  accent,
}: {
  icon: ReactNode
  value: ReactNode
  label: string
  accent?: boolean
}) {
  return (
    <div className={`stat-tile${accent ? ' stat-tile-accent' : ''}`}>
      <span className="stat-tile-icon" aria-hidden="true">
        {icon}
      </span>
      <strong className="stat-tile-value">{value}</strong>
      <span className="stat-tile-label">{label}</span>
    </div>
  )
}

export function ProgressPage() {
  const { locale, t } = useTranslation()
  const { isSignedIn, isLoaded } = useAppAuth()

  const serverStats = useQuery(
    api.learning.stats,
    isSignedIn ? {} : 'skip',
  )
  const serverContinue = useQuery(
    api.learning.continueLearning,
    isSignedIn ? {} : 'skip',
  )
  const serverNewSince = useQuery(
    api.learning.newSinceLastVisit,
    isSignedIn ? {} : 'skip',
  )
  const certificates = useQuery(
    api.learning.myCertificates,
    isSignedIn ? {} : 'skip',
  )
  const issueCertificate = useMutation(api.learning.issueCertificate)
  const markSeen = useMutation(api.learning.markSeen)

  const [anonDashboard, setAnonDashboard] = useState<DashboardStats | null>(
    null,
  )
  const [anonContinue, setAnonContinue] = useState<{
    courseSlug: string
    lessonSlug: string
  } | null>(null)
  const [anonNewChangelog, setAnonNewChangelog] = useState(0)
  const [issuing, setIssuing] = useState<string | null>(null)
  const [issueError, setIssueError] = useState(false)

  // Anonymous (and pre-auth) path: hydrate the dashboard from localStorage so the
  // page is useful immediately, even before Clerk resolves.
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (isSignedIn) return
    setAnonDashboard(computeAnonymousDashboard(catalog))

    // Continue-learning from the most recent locally-viewed lesson.
    const state = readLocalAnonymousState()
    const recentList = Object.entries(state.progress)
      .map(([slug, value]) => ({ slug, ...value }))
      .sort((a, b) => b.lastViewedAt - a.lastViewedAt)
    const recent = recentList.length > 0 ? recentList[0] : null
    if (recent) {
      const lesson = catalog.lessons.find((item) => item.slug === recent.slug)
      if (lesson) {
        setAnonContinue({
          courseSlug: lesson.courseSlug,
          lessonSlug: lesson.slug,
        })
      }
    }

    // New changelog entries since the last local visit timestamp.
    const lastVisitRaw = window.localStorage.getItem(lastVisitStorageName)
    const lastVisit = lastVisitRaw ? Number(lastVisitRaw) : 0
    const newCount = catalog.changelogEntries.filter(
      (entry) => Date.parse(`${entry.publishedAt}T00:00:00Z`) > lastVisit,
    ).length
    setAnonNewChangelog(newCount)
    window.localStorage.setItem(lastVisitStorageName, String(Date.now()))
  }, [isSignedIn, locale])

  // Signed-in path: advance the lastSeenAt window ONCE after the dashboard has
  // read the previous value (the query already captured `since`). The ref guard
  // prevents a reactive loop: markSeen patches lastSeenAt, which would otherwise
  // re-run newSinceLastVisit and re-trigger this effect.
  const seenMarked = useRef(false)
  useEffect(() => {
    if (
      !seenMarked.current &&
      isSignedIn &&
      serverNewSince !== undefined
    ) {
      seenMarked.current = true
      void markSeen({}).catch(() => {})
    }
  }, [isSignedIn, serverNewSince, markSeen])

  const dashboard: DashboardStats | null = isSignedIn
    ? serverStats
      ? {
          currentStreak: serverStats.currentStreak,
          longestStreak: serverStats.longestStreak,
          totalMinutes: serverStats.totalMinutes,
          lessonsCompleted: serverStats.lessonsCompleted,
          activeToday: serverStats.activeToday,
          courses: serverStats.courses,
        }
      : null
    : anonDashboard

  const certByCourse = useMemo(() => {
    const map = new Map<string, string>()
    for (const cert of certificates ?? []) {
      map.set(cert.courseSlug, cert.certificateId)
    }
    return map
  }, [certificates])

  const catalog = useCatalog()

  async function onIssueCertificate(courseSlug: string) {
    setIssueError(false)
    setIssuing(courseSlug)
    try {
      const result = await issueCertificate({ courseSlug })
      window.location.href = localizedPath('certificate', locale, {
        certificateId: result.certificateId,
      })
    } catch {
      setIssueError(true)
      setIssuing(null)
    }
  }

  const isLoading = isSignedIn
    ? serverStats === undefined
    : !isLoaded && anonDashboard === null

  // Continue-learning resolution (server slug -> localized link + title).
  const continueLesson = isSignedIn
    ? serverContinue
      ? {
          courseSlug: serverContinue.courseSlug ?? '',
          lessonSlug: serverContinue.lessonSlug,
          completed: serverContinue.status === 'completed',
        }
      : null
    : anonContinue
      ? { ...anonContinue, completed: false }
      : null

  const newReplyCount = isSignedIn ? (serverNewSince?.replyCount ?? 0) : 0
  const newChangelogCount = isSignedIn ? 0 : anonNewChangelog

  return (
    <main className="page-shell">
      <PageIntro
        eyebrow={t('pages.dashboard.eyebrow')}
        title={t('pages.dashboard.title')}
        text={t('pages.dashboard.text')}
      />

      {isLoading ? (
        <div className="dashboard-skeleton" aria-hidden="true">
          <div className="skeleton-row" />
          <div className="skeleton-row" />
          <div className="skeleton-block" />
        </div>
      ) : (
        <>
          <section className="stat-row" aria-label={t('pages.dashboard.title')}>
            <StatTile
              accent
              icon={<Flame size={20} />}
              value={dashboard?.currentStreak ?? 0}
              label={`${t('pages.dashboard.streakLabel')} (${
                dashboard?.activeToday
                  ? t('pages.dashboard.streakActiveToday')
                  : t('pages.dashboard.streakInactiveToday')
              })`}
            />
            <StatTile
              icon={<Clock size={20} />}
              value={dashboard?.totalMinutes ?? 0}
              label={t('pages.dashboard.minutesLabel')}
            />
            <StatTile
              icon={<CheckSquare size={20} />}
              value={dashboard?.lessonsCompleted ?? 0}
              label={t('pages.dashboard.lessonsLabel')}
            />
            <StatTile
              icon={<TrendingUp size={20} />}
              value={dashboard?.longestStreak ?? 0}
              label={t('pages.dashboard.longestLabel')}
            />
          </section>

          <div className="dashboard-grid">
            <section className="dashboard-card">
              <h2>{t('pages.dashboard.continueTitle')}</h2>
              {continueLesson && continueLesson.courseSlug ? (
                (() => {
                  const lesson = catalog.getLesson(
                    continueLesson.courseSlug,
                    continueLesson.lessonSlug,
                  )
                  return (
                    <a
                      className="primary-button"
                      href={localizedPath('lesson', locale, {
                        courseSlug: continueLesson.courseSlug,
                        lessonSlug: continueLesson.lessonSlug,
                      })}
                    >
                      {continueLesson.completed
                        ? t('pages.dashboard.continueNext')
                        : t('pages.dashboard.continueResume')}
                      {lesson ? `: ${lesson.title}` : ''}
                      <ArrowRight size={16} />
                    </a>
                  )
                })()
              ) : (
                <>
                  <p>{t('pages.dashboard.continueEmpty')}</p>
                  <a
                    className="ghost-button"
                    href={localizedPath('courses', locale)}
                  >
                    {t('pages.dashboard.continueCta')}
                  </a>
                </>
              )}
            </section>

            <section className="dashboard-card">
              <h2>{t('pages.dashboard.newSinceTitle')}</h2>
              {newReplyCount > 0 || newChangelogCount > 0 ? (
                <ul className="dashboard-news-list">
                  {newReplyCount > 0 ? (
                    <li>
                      <MessageSquareText size={16} aria-hidden="true" />
                      <span>
                        {newReplyCount} {t('pages.dashboard.newSinceReplies')}
                      </span>
                    </li>
                  ) : null}
                  {newChangelogCount > 0 ? (
                    <li>
                      <Sparkles size={16} aria-hidden="true" />
                      <a href={localizedPath('changelog', locale)}>
                        {newChangelogCount}{' '}
                        {t('pages.dashboard.newSinceChangelog')}
                      </a>
                    </li>
                  ) : null}
                </ul>
              ) : (
                <p>{t('pages.dashboard.newSinceEmpty')}</p>
              )}
            </section>
          </div>

          <section className="dashboard-courses">
            <h2>{t('pages.dashboard.coursesTitle')}</h2>
            <div className="course-progress-list">
              {(dashboard?.courses ?? []).map((courseStat) => {
                const course = catalog.getCourse(courseStat.courseSlug)
                const certId = certByCourse.get(courseStat.courseSlug)
                return (
                  <article
                    key={courseStat.courseSlug}
                    className="course-progress-row"
                  >
                    <div className="course-progress-head">
                      <strong>{course?.title ?? courseStat.courseSlug}</strong>
                      <span>
                        {courseStat.completed} / {courseStat.total}
                      </span>
                    </div>
                    <div
                      className="course-progress-bar"
                      role="progressbar"
                      aria-valuenow={courseStat.percent}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <span style={{ width: `${courseStat.percent}%` }} />
                    </div>
                    {courseStat.eligibleForCertificate ? (
                      certId ? (
                        <a
                          className="primary-button compact"
                          href={localizedPath('certificate', locale, {
                            certificateId: certId,
                          })}
                        >
                          <Award size={16} />
                          {t('pages.certificate.ctaView')}
                        </a>
                      ) : isSignedIn ? (
                        <button
                          className="primary-button compact"
                          type="button"
                          disabled={issuing === courseStat.courseSlug}
                          onClick={() =>
                            onIssueCertificate(courseStat.courseSlug)
                          }
                        >
                          <Award size={16} />
                          {issuing === courseStat.courseSlug
                            ? t('pages.certificate.ctaGetting')
                            : t('pages.certificate.ctaGet')}
                        </button>
                      ) : (
                        <a
                          className="ghost-button compact"
                          href={localizedPath('register', locale)}
                        >
                          <Lock size={16} />
                          {t('pages.certificate.ctaSignIn')}
                        </a>
                      )
                    ) : null}
                  </article>
                )
              })}
            </div>
            {issueError ? (
              <p className="form-status" role="status">
                {t('pages.certificate.ctaError')}
              </p>
            ) : null}
          </section>

          <ProPathCard
            surface="dashboard_upgrade"
            eyebrow={t('upsell.eyebrow')}
            title={t('upsell.dashboardTitle')}
            text={t('upsell.dashboardText')}
          />
        </>
      )}
    </main>
  )
}

export function CertificatePage({
  certificateId,
}: {
  certificateId: string
}) {
  const { locale, t } = useTranslation()
  const certificate = useQuery(api.learning.certificateById, { certificateId })
  const catalog = useCatalog()

  if (certificate === undefined) {
    return (
      <main className="page-shell narrow">
        <div className="certificate-skeleton" aria-hidden="true">
          <div className="skeleton-block" />
        </div>
        <p className="visually-hidden">{t('pages.certificate.loading')}</p>
      </main>
    )
  }

  if (certificate === null) {
    return (
      <UtilityPage
        eyebrow={t('pages.certificate.eyebrow')}
        title={t('pages.certificate.notFoundTitle')}
        text={t('pages.certificate.notFoundText')}
      />
    )
  }

  const course = catalog.getCourse(certificate.courseSlug)
  const issuedDate = new Date(certificate.issuedAt).toLocaleDateString(
    LOCALE_INTL[locale],
    { year: 'numeric', month: 'long', day: 'numeric' },
  )

  return (
    <main className="page-shell narrow">
      <article className="certificate-sheet">
        <header className="certificate-header">
          <img
            className="certificate-brand"
            src="/wordmark.webp"
            alt="Agentic School"
            width={254}
            height={28}
          />
          <span className="eyebrow">{t('pages.certificate.eyebrow')}</span>
        </header>
        <div className="certificate-body">
          <p className="certificate-label">{t('pages.certificate.issuedTo')}</p>
          <h1 className="certificate-recipient">{certificate.recipientName}</h1>
          <p className="certificate-course">
            {t('pages.certificate.forCourse')}{' '}
            <strong>{course?.title ?? certificate.courseSlug}</strong>
          </p>
          <dl className="certificate-meta">
            <div>
              <dt>{t('pages.certificate.issuedOn')}</dt>
              <dd>{issuedDate}</dd>
            </div>
            <div>
              <dt>{t('pages.certificate.idLabel')}</dt>
              <dd className="certificate-id">{certificate.certificateId}</dd>
            </div>
          </dl>
          <p className="certificate-verified">
            <ShieldCheck size={16} aria-hidden="true" />
            {t('pages.certificate.verified')}
          </p>
        </div>
        <footer className="certificate-actions">
          {course ? (
            <a
              className="primary-button"
              href={localizedPath('course', locale, {
                courseSlug: certificate.courseSlug,
              })}
            >
              {t('pages.certificate.viewCourse')}
              <ArrowRight size={16} />
            </a>
          ) : null}
          <a className="ghost-button" href={localizedPath('progress', locale)}>
            {t('pages.certificate.backToProgress')}
          </a>
        </footer>
      </article>
    </main>
  )
}

export function OnboardingPage() {
  const { t, locale } = useTranslation()
  const { isSignedIn } = useAppAuth()
  const navigate = useNavigate()
  const migrate = useMutation(api.migrations.migrateAnonymousState)
  const [status, setStatus] = useState(t('pages.utility.onboardingChecking'))

  // Onboarding is the post-auth landing target Clerk redirects to after sign-in
  // / sign-up (see Providers). It migrates any anonymous localStorage progress
  // into the freshly signed-in account, then SOFT-navigates the visitor to the
  // app dashboard so they land inside the app rather than on the marketing home
  // or a dead-end status screen. A guarded one-shot ref keeps the redirect from
  // firing more than once and keeps marketing browsing unaffected (a logged-in
  // user only hits this page via the auth flow, never by browsing).
  const redirected = useRef(false)
  const goToDashboard = useCallback(() => {
    if (redirected.current) return
    redirected.current = true
    void navigate({ to: localizedPath('progress', locale), replace: true })
  }, [navigate, locale])

  useEffect(() => {
    if (!isSignedIn) {
      setStatus(t('pages.utility.onboardingSignIn'))
      return
    }

    const raw = window.localStorage.getItem(pendingStateStorageName)
    if (!raw) {
      setStatus(t('pages.utility.onboardingNone'))
      goToDashboard()
      return
    }

    // Normalize the stored blob before sending. If the JSON itself is unparseable
    // garbage we clear it immediately (nothing the server could ever migrate), so
    // the onboarding never gets stuck retrying corrupt storage. Valid-but-partial
    // shapes are repaired by parseAnonymousState's schema defaults.
    let pendingState
    try {
      pendingState = parseAnonymousState(JSON.parse(raw))
    } catch {
      window.localStorage.removeItem(pendingStateStorageName)
      setStatus(t('pages.utility.onboardingFailed'))
      goToDashboard()
      return
    }

    void migrate({
      anonId: getCookie(anonIdCookieName) ?? undefined,
      pendingState,
      localStateHash: String(raw.length),
      clientCreatedAt: Date.now(),
    })
      .then(() => {
        window.localStorage.removeItem(pendingStateStorageName)
        setStatus(t('pages.utility.onboardingDone'))
        // Signup/activation conversion: the visitor just authenticated and their
        // anonymous progress was migrated into the new account. Fired once on
        // success (the server-side `signup_completed` is the source of truth).
        trackEvent('onboarding_completed', {})
        goToDashboard()
      })
      .catch(() => {
        // Transient failure (e.g. network). PRESERVE the stored state so a later
        // visit can retry the migration instead of silently dropping local data.
        // We still forward to the dashboard so the user is never stranded; the
        // preserved blob is retried on a future visit.
        setStatus(t('pages.utility.onboardingFailed'))
        goToDashboard()
      })
  }, [isSignedIn, migrate, t, goToDashboard])

  return (
    <UtilityPage
      eyebrow={t('pages.utility.onboardingEyebrow')}
      title={t('pages.utility.onboardingTitle')}
      text={status}
    />
  )
}

export function AdminPage() {
  const { isLoaded, isSignedIn } = useAppAuth()
  const access = useQuery(api.admin.access, isSignedIn ? {} : 'skip')
  const dashboard = useQuery(api.admin.dashboard, access?.allowed ? {} : 'skip')
  const content = useQuery(
    api.cms.adminContentOverview,
    access?.allowed ? {} : 'skip',
  )
  const signups = useQuery(
    api.forms.newsletterAdminList,
    access?.allowed ? {} : 'skip',
  )
  const contacts = useQuery(
    api.forms.contactAdminList,
    access?.allowed ? {} : 'skip',
  )
  const auditLogs = useQuery(api.admin.auditLogs, access?.allowed ? {} : 'skip')
  const upsertCourse = useMutation(api.cms.upsertCourse)
  const upsertLesson = useMutation(api.cms.upsertLesson)
  const upsertResource = useMutation(api.cms.upsertResource)
  const upsertChangelog = useMutation(api.cms.upsertChangelogEntry)
  const upsertTopic = useMutation(api.community.upsertTopic)
  const moderateComment = useMutation(api.comments.moderate)
  const resolveReport = useMutation(api.comments.resolveReport)
  const updateContactStatus = useMutation(api.forms.updateContactStatus)
  const updateSiteSetting = useMutation(api.admin.updateSiteSetting)
  const [status, setStatus] = useState<string | null>(null)

  if (!isLoaded)
    return (
      <UtilityPage
        eyebrow="Admin"
        title="Admin wird geprüft"
        text="Die Berechtigung wird geladen."
      />
    )
  if (!isSignedIn || access?.allowed === false) {
    return (
      <UtilityPage
        eyebrow="Admin"
        title="Admin-Bereich geschützt"
        text="Melde dich mit einem Admin-Konto an. Rollen und Admin-Daten werden serverseitig in Convex geprüft."
      />
    )
  }
  if (!access)
    return (
      <UtilityPage
        eyebrow="Admin"
        title="Admin wird geprüft"
        text="Die Convex-Rolle wird geladen."
      />
    )

  async function runAdminAction(
    action: () => Promise<unknown>,
    success: string,
  ) {
    setStatus('Speichern...')
    try {
      await action()
      setStatus(success)
    } catch {
      setStatus(
        'Die Admin-Aktion wurde abgelehnt. Prüfe Rolle, Pflichtfelder und Validierung.',
      )
    }
  }

  async function onCourseSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    await runAdminAction(
      () =>
        upsertCourse({
          slug: String(form.get('slug') ?? ''),
          title: String(form.get('title') ?? ''),
          subtitle: String(form.get('subtitle') ?? ''),
          description: String(form.get('description') ?? ''),
          seoTitle: String(form.get('seoTitle') ?? ''),
          seoDescription: String(form.get('seoDescription') ?? ''),
          status:
            String(form.get('status')) === 'published' ? 'published' : 'draft',
          order: Number(form.get('order') ?? 1),
          level: 'business',
          tags: String(form.get('tags') ?? '')
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean),
        }),
      'Kurs wurde in Convex gespeichert.',
    )
  }

  async function onLessonSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const courseId = String(form.get('courseId') ?? '')
    if (!courseId) {
      setStatus('Lege zuerst einen Kurs in Convex an.')
      return
    }
    await runAdminAction(
      () =>
        upsertLesson({
          courseId: courseId as NonNullable<
            typeof content
          >['courses'][number]['_id'],
          slug: String(form.get('slug') ?? ''),
          title: String(form.get('title') ?? ''),
          subtitle: String(form.get('subtitle') ?? ''),
          summary: String(form.get('summary') ?? ''),
          body: String(form.get('body') ?? ''),
          transcript: String(form.get('transcript') ?? ''),
          whatCovered: String(form.get('whatCovered') ?? '')
            .split('\n')
            .map((item) => item.trim())
            .filter(Boolean),
          youtubeId: String(form.get('youtubeId') ?? ''),
          youtubeThumbnailUrl: String(form.get('youtubeThumbnailUrl') ?? ''),
          videoDuration: String(form.get('videoDuration') ?? ''),
          videoUploadDate: String(form.get('videoUploadDate') ?? ''),
          order: Number(form.get('order') ?? 1),
          level: 'business',
          toolTags: String(form.get('toolTags') ?? '')
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean),
          topicTags: String(form.get('topicTags') ?? '')
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean),
          seoTitle: String(form.get('seoTitle') ?? ''),
          seoDescription: String(form.get('seoDescription') ?? ''),
          status:
            String(form.get('status')) === 'published' ? 'published' : 'draft',
        }),
      'Lektion wurde in Convex gespeichert.',
    )
  }

  return (
    <main className="page-shell">
      <PageIntro
        eyebrow="Admin"
        title="Campus Cockpit"
        text="CMS, Moderation, Newsletter, Kontaktanfragen, Audit Logs und Content Checks."
      />
      {status ? (
        <p className="form-status" role="status">
          {status}
        </p>
      ) : null}
      <div className="admin-grid">
        <article className="admin-panel">
          <GraduationCap size={22} />
          <h2>Live Status</h2>
          <div className="metric-grid">
            <span>
              Kurse <strong>{content?.courses.length ?? 0}</strong>
            </span>
            <span>
              Lektionen <strong>{content?.lessons.length ?? 0}</strong>
            </span>
            <span>
              Newsletter{' '}
              <strong>
                {signups?.length ?? dashboard?.signups.length ?? 0}
              </strong>
            </span>
            <span>
              Kontakt{' '}
              <strong>
                {contacts?.length ?? dashboard?.contacts.length ?? 0}
              </strong>
            </span>
            <span>
              Meldungen <strong>{dashboard?.reports.length ?? 0}</strong>
            </span>
            <span>
              Audit <strong>{auditLogs?.length ?? 0}</strong>
            </span>
          </div>
        </article>

        <AdminBillingPanel />

        <AdminEmailPanel />

        <article className="admin-panel">
          <h2>Kurs CMS</h2>
          <form className="form-grid compact-form" onSubmit={onCourseSubmit}>
            <input
              name="title"
              aria-label="Kurstitel"
              placeholder="Kurstitel"
              required
            />
            <input
              name="slug"
              aria-label="Kurs Slug"
              placeholder="kurs-slug"
              required
            />
            <input
              name="subtitle"
              aria-label="Untertitel"
              placeholder="Untertitel"
              required
            />
            <textarea
              name="description"
              aria-label="Beschreibung"
              placeholder="Beschreibung"
              required
            />
            <input
              name="seoTitle"
              aria-label="SEO Titel"
              placeholder="SEO Titel"
              required
            />
            <textarea
              name="seoDescription"
              aria-label="SEO Beschreibung"
              placeholder="SEO Beschreibung"
              required
            />
            <input
              name="tags"
              aria-label="Tags"
              placeholder="agenten, roi, workflow"
            />
            <input
              name="order"
              aria-label="Reihenfolge"
              type="number"
              defaultValue={
                content?.courses.length ? content.courses.length + 1 : 1
              }
            />
            <select name="status" aria-label="Status" defaultValue="draft">
              <option value="draft">Entwurf</option>
              <option value="published">Veröffentlicht</option>
            </select>
            <button className="primary-button" type="submit">
              Kurs speichern
            </button>
          </form>
        </article>

        <article className="admin-panel">
          <h2>Lektions CMS</h2>
          <form className="form-grid compact-form" onSubmit={onLessonSubmit}>
            <select name="courseId" aria-label="Kurs" required>
              <option value="">Kurs wählen</option>
              {content?.courses.map((course) => (
                <option key={course._id} value={course._id}>
                  {course.title}
                </option>
              ))}
            </select>
            <input
              name="title"
              aria-label="Lektionstitel"
              placeholder="Lektionstitel"
              required
            />
            <input
              name="slug"
              aria-label="Lektion Slug"
              placeholder="lektion-slug"
              required
            />
            <input
              name="subtitle"
              aria-label="Untertitel"
              placeholder="Untertitel"
              required
            />
            <textarea
              name="summary"
              aria-label="Zusammenfassung"
              placeholder="Zusammenfassung"
              required
            />
            <textarea
              name="body"
              aria-label="Lektionstext"
              placeholder="Lektionstext mit internem Link wie [Kurse](/kurse)"
              required
            />
            <textarea
              name="transcript"
              aria-label="Transkript"
              placeholder="Transkript"
              required
            />
            <textarea
              name="whatCovered"
              aria-label="Was du lernst"
              placeholder="Ein Outcome pro Zeile"
              required
            />
            <input
              name="youtubeId"
              aria-label="YouTube ID"
              placeholder="YouTube ID"
              required
            />
            <input
              name="youtubeThumbnailUrl"
              aria-label="Thumbnail URL"
              placeholder="https://..."
              required
            />
            <input
              name="videoDuration"
              aria-label="Videodauer"
              placeholder="PT12M"
              required
            />
            <input
              name="videoUploadDate"
              aria-label="Upload Datum"
              placeholder="2026-05-26"
              required
            />
            <input
              name="order"
              aria-label="Reihenfolge"
              type="number"
              defaultValue={
                content?.lessons.length ? content.lessons.length + 1 : 1
              }
            />
            <input
              name="toolTags"
              aria-label="Tool Tags"
              placeholder="Codex, Claude Code"
            />
            <input
              name="topicTags"
              aria-label="Topic Tags"
              placeholder="Setup, ROI"
            />
            <input
              name="seoTitle"
              aria-label="SEO Titel"
              placeholder="SEO Titel"
              required
            />
            <textarea
              name="seoDescription"
              aria-label="SEO Beschreibung"
              placeholder="SEO Beschreibung"
              required
            />
            <select name="status" aria-label="Status" defaultValue="draft">
              <option value="draft">Entwurf</option>
              <option value="published">Veröffentlicht</option>
            </select>
            <button className="primary-button" type="submit">
              Lektion speichern
            </button>
          </form>
        </article>

        <AdminQuickCreate
          title="Ressourcen CMS"
          fields={['title', 'slug', 'shortDescription', 'body', 'tags']}
          button="Ressource speichern"
          onSubmit={(form) =>
            runAdminAction(
              () =>
                upsertResource({
                  slug: String(form.get('slug') ?? ''),
                  title: String(form.get('title') ?? ''),
                  type: 'guide',
                  shortDescription: String(form.get('shortDescription') ?? ''),
                  body: String(form.get('body') ?? ''),
                  difficulty: 'einsteiger',
                  tags: String(form.get('tags') ?? '')
                    .split(',')
                    .map((tag) => tag.trim())
                    .filter(Boolean),
                  relatedLessonIds: [],
                  status: 'published',
                }),
              'Ressource wurde gespeichert.',
            )
          }
        />

        <AdminQuickCreate
          title="Changelog CMS"
          fields={['tool', 'title', 'slug', 'body', 'impact', 'tags']}
          button="Changelog speichern"
          onSubmit={(form) =>
            runAdminAction(
              () =>
                upsertChangelog({
                  slug: String(form.get('slug') ?? ''),
                  tool: String(form.get('tool') ?? ''),
                  title: String(form.get('title') ?? ''),
                  body: String(form.get('body') ?? ''),
                  impact: String(form.get('impact') ?? ''),
                  tags: String(form.get('tags') ?? '')
                    .split(',')
                    .map((tag) => tag.trim())
                    .filter(Boolean),
                  status: 'published',
                }),
              'Changelog-Eintrag wurde gespeichert.',
            )
          }
        />

        <AdminQuickCreate
          title="Community Themen"
          fields={['title', 'description', 'order']}
          button="Thema speichern"
          onSubmit={(form) =>
            runAdminAction(
              () =>
                upsertTopic({
                  title: String(form.get('title') ?? ''),
                  description: String(form.get('description') ?? ''),
                  order: Number(form.get('order') ?? 1),
                }),
              'Community-Thema wurde gespeichert.',
            )
          }
        />

        <article className="admin-panel">
          <h2>Moderation</h2>
          <div className="admin-list">
            {dashboard?.comments.map((comment) => (
              <div className="admin-row" key={comment._id}>
                <span>{comment.body.slice(0, 120)}</span>
                <button
                  className="ghost-button compact"
                  type="button"
                  onClick={() =>
                    void runAdminAction(
                      () =>
                        moderateComment({
                          commentId: comment._id,
                          status: 'hidden',
                        }),
                      'Kommentar wurde verborgen.',
                    )
                  }
                >
                  Verbergen
                </button>
              </div>
            ))}
            {dashboard?.reports.map((report) => (
              <div className="admin-row" key={report._id}>
                <span>{report.reason}</span>
                <button
                  className="ghost-button compact"
                  type="button"
                  onClick={() =>
                    void runAdminAction(
                      () =>
                        resolveReport({
                          reportId: report._id,
                          status: 'resolved',
                        }),
                      'Meldung wurde geschlossen.',
                    )
                  }
                >
                  Schliessen
                </button>
              </div>
            ))}
          </div>
        </article>

        <article className="admin-panel">
          <h2>Newsletter und Kontakt</h2>
          <div className="admin-list">
            {signups?.slice(0, 5).map((signup) => (
              <div className="admin-row" key={signup._id}>
                <span>{signup.email}</span>
                <small>{signup.status}</small>
              </div>
            ))}
            {contacts?.slice(0, 5).map((contact) => (
              <div className="admin-row" key={contact._id}>
                <span>
                  {contact.email} - {contact.topic}
                </span>
                <button
                  className="ghost-button compact"
                  type="button"
                  onClick={() =>
                    void runAdminAction(
                      () =>
                        updateContactStatus({
                          contactId: contact._id,
                          status: 'read',
                        }),
                      'Kontaktanfrage wurde markiert.',
                    )
                  }
                >
                  Gelesen
                </button>
              </div>
            ))}
          </div>
        </article>

        <article className="admin-panel">
          <h2>Site Einstellungen und Audit</h2>
          <form
            className="form-grid compact-form"
            onSubmit={(event) => {
              event.preventDefault()
              const form = new FormData(event.currentTarget)
              void runAdminAction(
                () =>
                  updateSiteSetting({
                    key: String(form.get('key') ?? ''),
                    value: String(form.get('value') ?? ''),
                  }),
                'Site-Einstellung wurde gespeichert.',
              )
            }}
          >
            <input
              name="key"
              aria-label="Setting Key"
              placeholder="maintenance.banner"
              required
            />
            <input
              name="value"
              aria-label="Setting Value"
              placeholder="Wert"
              required
            />
            <button className="primary-button" type="submit">
              Einstellung speichern
            </button>
          </form>
          <div className="admin-list">
            {auditLogs?.slice(0, 8).map((log) => (
              <div className="admin-row" key={log._id}>
                <span>{log.action}</span>
                <small>
                  {new Intl.DateTimeFormat('de-CH').format(log.createdAt)}
                </small>
              </div>
            ))}
          </div>
        </article>
      </div>
    </main>
  )
}

function AdminQuickCreate({
  title,
  fields,
  button,
  onSubmit,
}: {
  title: string
  fields: string[]
  button: string
  onSubmit: (form: FormData) => void
}) {
  return (
    <article className="admin-panel">
      <h2>{title}</h2>
      <form
        className="form-grid compact-form"
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit(new FormData(event.currentTarget))
        }}
      >
        {fields.map((field) =>
          ['body', 'impact', 'description', 'shortDescription'].includes(
            field,
          ) ? (
            <textarea
              key={field}
              name={field}
              aria-label={field}
              placeholder={field}
              required
            />
          ) : (
            <input
              key={field}
              name={field}
              aria-label={field}
              placeholder={field}
              required
            />
          ),
        )}
        <button className="primary-button" type="submit">
          {button}
        </button>
      </form>
    </article>
  )
}

function UtilityPage({
  eyebrow,
  title,
  text,
}: {
  eyebrow: string
  title: string
  text: string
}) {
  return (
    <main className="page-shell narrow">
      <PageIntro eyebrow={eyebrow} title={title} text={text} />
    </main>
  )
}

/**
 * Human and agent-readable documentation for the machine surface: the read-only
 * JSON API, the OpenAPI spec, llms.txt and the `.md`-per-page convention. All
 * endpoint paths and copy are localized; absolute curl examples use the resolved
 * site origin. Linked from the footer in both locales and from llms.txt.
 */
export function ApiDocsPage() {
  const { locale, dict } = useTranslation()
  const copy = dict.pages.apiDocs
  const base = getSiteUrl()
  // The whole agent surface is locale-prefixed the same way every page URL is:
  // EN at the root, DE under `/de`. Derive every path from the shared
  // `localePrefix` / `localizedPath` helpers instead of `locale === 'de'`
  // ternaries so the docs can never drift from the real routing (e.g. the DE
  // API base is `/api/v1/de`, and the `.md` example reuses the course route).
  const apiBase = `/api/v1${localePrefix(locale)}`
  const llmsPrefix = localePrefix(locale)

  const endpoints: Array<{ method: 'GET'; path: string; desc: string }> = [
    { method: 'GET', path: `${apiBase}/index.json`, desc: copy.ep.discovery },
    { method: 'GET', path: '/api/v1/openapi.json', desc: copy.ep.openapi },
    { method: 'GET', path: `${apiBase}/courses.json`, desc: copy.ep.courses },
    {
      method: 'GET',
      path: `${apiBase}/courses/{slug}.json`,
      desc: copy.ep.course,
    },
    { method: 'GET', path: `${apiBase}/lessons.json`, desc: copy.ep.lessons },
    {
      method: 'GET',
      path: `${apiBase}/lessons/{slug}.json`,
      desc: copy.ep.lesson,
    },
    {
      method: 'GET',
      path: `${apiBase}/fundamentals.json`,
      desc: copy.ep.fundamentals,
    },
    { method: 'GET', path: `${apiBase}/articles.json`, desc: copy.ep.articles },
    { method: 'GET', path: `${apiBase}/builds.json`, desc: copy.ep.builds },
    {
      method: 'GET',
      path: `${apiBase}/resources.json`,
      desc: copy.ep.resources,
    },
    {
      method: 'GET',
      path: `${apiBase}/changelog.json`,
      desc: copy.ep.changelog,
    },
    {
      method: 'GET',
      path: `${apiBase}/search-index.json`,
      desc: copy.ep.searchIndex,
    },
  ]

  const curlBase = `${base}${apiBase}`
  const courseMdPath = localizedPath('course', locale, {
    courseSlug: 'foundations',
  })
  const curlExamples = [
    `curl ${curlBase}/courses.json`,
    `curl ${curlBase}/lessons/your-first-project-scaffolding-dev-server-git-and-github.json`,
    `curl ${base}${courseMdPath}.md`,
    `curl ${base}${llmsPrefix}/llms.txt`,
  ]

  return (
    <main className="page-shell narrow">
      <PageIntro eyebrow={copy.eyebrow} title={copy.title} text={copy.intro} />
      <article className="editorial-block legal-block">
        <section>
          <h2>{copy.conventions.title}</h2>
          <ul className="legal-list">
            {copy.conventions.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2>{copy.endpointsTitle}</h2>
          <ul className="legal-list">
            {endpoints.map((ep) => (
              <li key={ep.path}>
                <code>
                  {ep.method} {ep.path}
                </code>{' '}
                - {ep.desc}
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2>{copy.examplesTitle}</h2>
          <pre className="api-docs-pre">
            <code>{curlExamples.join('\n')}</code>
          </pre>
        </section>

        <section>
          <h2>{copy.markdownTitle}</h2>
          <p>{copy.markdownBody}</p>
        </section>

        <section>
          <h2>{copy.fairUseTitle}</h2>
          <p>{copy.fairUseBody}</p>
        </section>

        <section>
          <h2>{copy.linksTitle}</h2>
          <ul className="legal-list">
            <li>
              <a href={`${llmsPrefix}/llms.txt`}>llms.txt</a>
            </li>
            <li>
              <a href={`${llmsPrefix}/llms-full.txt`}>llms-full.txt</a>
            </li>
            <li>
              <a href="/api/v1/openapi.json">OpenAPI 3.1</a>
            </li>
            <li>
              <a href={`${apiBase}/index.json`}>{copy.ep.discovery}</a>
            </li>
          </ul>
        </section>
      </article>
    </main>
  )
}

export function LegalPage({
  kind,
}: {
  kind: 'impressum' | 'datenschutz' | 'cookies' | 'agb' | 'barrierefreiheit'
}) {
  const { t, formatDate } = useTranslation()
  // Every locale has its own complete legal text (src/content/legal/*); the
  // route loader sends down only the requested document (page-data.ts).
  const content = useLegalDocument()
  return (
    <main className="page-shell narrow">
      <PageIntro
        eyebrow={t('pages.legal.eyebrow')}
        title={content.title}
        text={content.intro}
      />
      <article className="editorial-block legal-block">
        <p className="legal-updated">
          {t('pages.legal.updatedPrefix')} {formatDate(content.updated)}
        </p>
        {content.sections.map((section) => (
          <section key={section.title}>
            <h2>{section.title}</h2>
            <p>{section.body}</p>
            {section.list ? (
              <ul className="legal-list">
                {section.list.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
        {kind === 'cookies' ? (
          <div className="legal-actions">
            <CookieSettingsButton />
          </div>
        ) : null}
      </article>
    </main>
  )
}

/**
 * Re-opens the global cookie-consent dialog so visitors can review or
 * withdraw consent at any time (GDPR/ePrivacy: withdrawing must be as easy
 * as giving). Dispatches a window event the CookieBanner listens for.
 */
function CookieSettingsButton() {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      className="primary-button"
      onClick={() => window.dispatchEvent(new Event('open-cookie-settings'))}
    >
      {t('pages.legal.cookieButton')}
    </button>
  )
}

